(() => {
  const STATUS = {
    OPEN: 'open',
    SELECTED: 'selected',
    OWNED: 'owned',
    SKIPPED: 'skipped',
    SELF_OPEN: 'self_open',
    SELF_DONE: 'self_done',
  };

  const PRIORITY_RANK = {
    optional: 1,
    recommended: 2,
    essential: 3,
  };

  const OLD_STATUS_MAP = {
    covered_selected: STATUS.SELECTED,
    covered_owned: STATUS.OWNED,
    skipped_optional: STATUS.SKIPPED,
    covered_self: STATUS.SELF_DONE,
    open_self: STATUS.SELF_OPEN,
    open_equipment: STATUS.OPEN,
  };

  const escapeHTML = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const toKebab = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const getStorage = () => {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  };

  const getStoredState = (key) => {
    const storage = getStorage();
    if (!storage) return null;
    try {
      return JSON.parse(storage.getItem(key));
    } catch (error) {
      return null;
    }
  };

  const setStoredState = (key, state) => {
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.setItem(key, JSON.stringify(state));
    } catch (error) {
    }
  };

  const removeStoredState = (key) => {
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.removeItem(key);
    } catch (error) {
    }
  };

  const unique = (items) => [...new Set(items.filter(Boolean))];

  const initBuilder = (root) => {
    if (!root || root.dataset.builderReady === 'true') return;
    root.dataset.builderReady = 'true';

    const configNode = root.querySelector('[data-asb-config]');
    if (!configNode) return;

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      root.dataset.builderError = 'config';
      return;
    }

    const language = String(document.documentElement.lang || 'nl').toLowerCase().split('-')[0];
    const exactTranslations = {
      en: {
        'PERSOONLIJKE KEUZEHULP': 'PERSONAL SELECTION GUIDE',
        'Noodpakket samenstellen voor jouw situatie': 'Build an emergency kit for your situation',
        'Kies waarvoor je je wilt voorbereiden. We begeleiden je per onderdeel naar een passende selectie en laten duidelijk zien wat je zelf nog moet regelen.': 'Choose what you want to prepare for. We guide you to a suitable selection for each need and clearly show what you still need to arrange yourself.',
        'Alleen kiezen wat je nog mist': 'Choose only what you still need',
        'Producten eenvoudig vergelijken': 'Compare products easily',
        'Alles overzichtelijk in één selectie': 'Everything clearly arranged in one selection',
        'COMBINEER & BESPAAR': 'COMBINE & SAVE',
        'Bekijk hoe het bundelvoordeel werkt': 'See how tiered savings work',
        'Kies je situatie': 'Choose your situation',
        '30 dagen retour': '30-day returns',
        'Gratis verzending vanaf €75': 'Free shipping from €75',
        '72-uurs checklist openen': 'Open the 72-hour checklist',
        'Voortgang keuzehulp': 'Selection guide progress',
        'Situatie': 'Situation',
        'Uitrusting': 'Equipment',
        'Winkelwagen': 'Cart',
        'Stap 1 van 3': 'Step 1 of 3',
        'Kies je situatie(s)': 'Choose your situation(s)',
        'Waarvoor wil je je voorbereiden? Je kunt meerdere situaties aanklikken. Gedeelde onderdelen worden samengevoegd, zodat je niets dubbel kiest.': 'What do you want to prepare for? You can select multiple situations. Shared items are combined so you do not choose anything twice.',
        'Voor wie stel je dit samen?': 'Who are you preparing this for?',
        'Eén korte vraag. Daarna zie je in één overzicht wat bij jouw situatie past.': 'One quick question. You will then see everything that suits your situation in one overview.',
        'Voor hoeveel personen?': 'For how many people?',
        "Dit bepaalt de aantallen in je pakket — drinkwater, warmte en EHBO — en welke extra's zinvol zijn.": 'This determines the quantities in your kit—including drinking water, warmth and first aid—and which extras make sense.',
        '1 persoon': '1 person',
        '2 personen': '2 people',
        '3 personen': '3 people',
        '4 personen': '4 people',
        '5 of meer': '5 or more',
        'Bij 72 uur is dit 9 liter drinkwater voor 1 persoon.': 'For 72 hours, this means 9 litres of drinking water for 1 person.',
        'Hoe lang blijf je buiten zonder stroom?': 'How long will you be outdoors without power?',
        'Dagtocht': 'Day trip',
        'Overnachting': 'Overnight stay',
        'Meerdere dagen': 'Several days',
        'Bij langere duur verschuift het advies naar extra energie, waterfiltering en warmte.': 'For longer periods, the recommendation shifts towards extra power, water filtration and warmth.',
        'Verder naar uitrusting': 'Continue to equipment',
        'Stap 2 van 3 · Jouw aanbevelingen': 'Step 2 of 3 · Your recommendations',
        'Jouw aanbevelingen': 'Your recommendations',
        'HANDIGE EXTRA’S': 'USEFUL EXTRAS',
        'MAAK JE UITRUSTING NOG PRAKTISCHER': 'MAKE YOUR EQUIPMENT EVEN MORE PRACTICAL',
        'Deze producten zijn handig, maar niet essentieel voor je basisadvies.': 'These products are useful, but not essential to your basic recommendation.',
        'Dit regel je zelf': 'Arrange these yourself',
        'Denk daarnaast aan drinkwater, eten, medicatie, persoonlijke benodigdheden en belangrijke documenten.': 'Also remember drinking water, food, medication, personal essentials and important documents.',
        'Bekijk de volledige 72-uurs checklist →': 'View the complete 72-hour checklist →',
        'Bekijk mijn selectie': 'Review my selection',
        'Opnieuw beginnen': 'Start again',
        'Stap 3 van 3 · Winkelwagen': 'Step 3 of 3 · Cart',
        'Jouw selectie': 'Your selection',
        'Controleer aantallen en varianten. Daarna ga je veilig naar afrekenen.': 'Check quantities and variants, then continue securely to checkout.',
        'Je selectie is nog leeg.': 'Your selection is still empty.',
        'Terug naar uitrusting': 'Back to equipment',
        'In je pakket': 'In your kit',
        '0 producten': '0 products',
        'Subtotaal producten': 'Product subtotal',
        'Veilig afrekenen': 'Secure checkout',
        'Naar checkout': 'Go to checkout',
        'Uitrusting aanpassen': 'Edit equipment',
        'Zekerheden bij bestellen': 'Ordering assurances',
        'Veilig betalen': 'Secure payment',
        'Dit koop je niet bij ons': 'Not sold by us',
        'Wat je zelf nog regelt': 'What you still need to arrange',
        'Je bestelling dekt de uitrusting. Deze punten horen bij je voorbereiding maar verkopen we niet; regel ze zelf na je bestelling.': 'Your order covers the equipment. These items are part of your preparation but are not sold by us; arrange them yourself after ordering.',
        'Nog geen situatie gekozen': 'No situation selected yet',
        'Kies een situatie om te beginnen.': 'Choose a situation to get started.',
        'Bekijk alle gekozen producten': 'View all selected products',
        'Bekijk selectie': 'View selection',
        'Gekozen producten': 'Selected products',
        'Sluit selectie': 'Close selection',
        'Selectie controleren': 'Review selection',
        'Sluit productdetails': 'Close product details',
        'Productdetails': 'Product details',
        'Ander model kiezen': 'Choose another model',
        'Kies een ander model': 'Choose another model',
        'Sluit modelkeuze': 'Close model selection',
        'Sluit kortingsstaffel': 'Close savings tiers',
        'Zo werkt het staffelvoordeel': 'How tiered savings work',
        'Kwalificerende artikelen tellen per stuk mee. Producten die al in je winkelwagen staan, kunnen ook meetellen. De definitieve korting wordt automatisch toegepast bij het afrekenen.': 'Qualifying items count per unit. Qualifying products already in your cart may also count. The final discount is applied automatically at checkout.',
        '72 uur thuis voorbereid': 'Prepared at home for 72 hours',
        '72 uur thuis': '72 hours at home',
        'Voor wanneer stroom, water, internet of winkels tijdelijk niet beschikbaar zijn.': 'For temporary disruptions to power, water, internet or access to shops.',
        'Thuis': 'Home',
        'Stroomuitval': 'Power outage',
        'Licht, informatie en bereik behouden wanneer de elektriciteit uitvalt.': 'Maintain light, information and communication when the power goes out.',
        'Energie': 'Power',
        'Waterstoring': 'Water outage',
        'Drinkwater valt uit': 'Drinking water outage',
        'Water kunnen opslaan en alternatieven hebben wanneer kraanwater tijdelijk niet beschikbaar is.': 'Store water and have alternatives when tap water is temporarily unavailable.',
        'Drinkwater zelf regelen en uitrusting kiezen voor opslag, transport en aanvullende filtering.': 'Arrange drinking water yourself and choose equipment for storage, transport and additional filtration.',
        'Water': 'Water',
        'Snel moeten vertrekken': 'Leave quickly',
        'Belangrijke spullen draagbaar en direct bereikbaar bij elkaar houden.': 'Keep important items portable, together and immediately accessible.',
        'Vertrek': 'Evacuation',
        'Auto en onderweg': 'Car and travel',
        'Auto & onderweg': 'Car & travel',
        'Voor pech, vertraging of onverwacht langer onderweg zijn.': 'For breakdowns, delays or unexpectedly long journeys.',
        'Voor pech, vertraging of onverwacht langere tijd onderweg zijn.': 'For breakdowns, delays or unexpectedly long journeys.',
        'Onderweg': 'Travel',
        'Buiten zonder stroom': 'Outdoors without power',
        'Compacte uitrusting voor kamperen, wandelen en buiten verblijven.': 'Compact equipment for camping, hiking and spending time outdoors.',
        'Verwarming valt uit': 'Heating outage',
        'Warm en voorbereid blijven wanneer verwarming of elektriciteit langere tijd uitvalt.': 'Stay warm and prepared during an extended heating or power outage.',
        'Warmte': 'Warmth',
        'Noodradio en informatie': 'Emergency radio and information',
        'Energie & licht': 'Power & lighting',
        'Stroom en licht die het doen zonder stopcontact.': 'Power and lighting that work without a mains connection.',
        'Water & filters': 'Water & filters',
        'Filteren, bewaren en meenemen — van filterfles tot jerrycan. Gevuld drinkwater regel je zelf.': 'Filter, store and carry water—from filter bottles to jerrycans. Arrange filled drinking water yourself.',
        'EHBO & veiligheid': 'First aid & safety',
        'Eerste hulp, signalering en zichtbaarheid voor thuis, onderweg en buiten.': 'First aid, signalling and visibility at home, on the road and outdoors.',
        'Kamperen & outdoor': 'Camping & outdoors',
        'Slapen, koken en comfort voor langere tijd buiten.': 'Sleeping, cooking and comfort for longer periods outdoors.',
        'Gereedschap & survival tools': 'Tools & survival gear',
        'Praktisch gereedschap dat je dagelijks of onderweg bij je hebt.': 'Practical tools to keep with you every day or while travelling.',
        'Tassen & onderweg': 'Bags & travel',
        'Maak je selectie draagbaar en geschikt voor auto, vertrek of vertraging.': 'Make your selection portable and suitable for the car, evacuation or delays.',
        'Bekijk alles voor onderweg': 'View all travel equipment',
        'Radio-ontvangst en noodinformatie als internet of stroom wegvalt.': 'Radio reception and emergency information when internet or power is unavailable.',
        'Powerbank voor telefoon': 'Phone power bank',
        'Reserve-energie voor telefoon en kleine USB-apparaten. Kabels controleer je apart.': 'Backup power for your phone and small USB devices. Check cables separately.',
        'Zaklamp of hoofdlamp': 'Torch or headlamp',
        'Losse verlichting zonder stopcontact.': 'Portable lighting without a mains connection.',
        'Wateropslag en meenemen': 'Water storage and transport',
        'Waterzakken en flessen helpen bij opslag of transport. Vullen blijft zelf regelen.': 'Water bags and bottles help with storage and transport. You still need to fill them yourself.',
        'Aanvullende waterfiltering': 'Additional water filtration',
        'Een filter kan handig zijn bij geschikte bronnen, maar vervangt geen drinkwatervoorraad.': 'A filter can be useful with suitable sources, but does not replace stored drinking water.',
        'EHBO-set': 'First aid kit',
        'Een gecontroleerde EHBO-basis voor kleine verwondingen en onverwachte situaties.': 'A checked first aid foundation for minor injuries and unexpected situations.',
        'Noodsignalering': 'Emergency signalling',
        'Maak jezelf hoorbaar of vindbaar.': 'Make yourself heard or easier to find.',
        'Warm blijven': 'Stay warm',
        'Thermische noodwarmte naast eigen kleding en dekens.': 'Emergency thermal warmth to complement your own clothing and blankets.',
        'Bereiden zonder netstroom': 'Cooking without mains power',
        'Uitrusting om buiten of veilig geventileerd te bereiden. Eten en brandstof controleer je zelf.': 'Equipment for cooking outdoors or in a safely ventilated area. Arrange food and fuel yourself.',
        'Multitool en kleine reparaties': 'Multitool and minor repairs',
        'Voor openen, vastzetten en eenvoudige noodoplossingen.': 'For opening, fastening and simple emergency repairs.',
        'Navigatie zonder internet': 'Navigation without internet',
        'Een offline route, kaart of kompas naast je telefoon.': 'An offline route, map or compass alongside your phone.',
        'Tas of draagset': 'Bag or carry set',
        'Houd de gekozen uitrusting draagbaar bij elkaar.': 'Keep the selected equipment together and portable.',
        'Auto en pechvoorbereiding': 'Car and breakdown preparation',
        'Gerichte uitrusting voor pech, accu, banden en vertraging.': 'Targeted equipment for breakdowns, batteries, tyres and delays.',
        'Handige extra': 'Useful extra',
        'Voor extra zekerheid': 'For extra reassurance',
        'Comfort onderweg': 'Comfort on the road',
        'Opgeslagen drinkwater': 'Stored drinking water',
        'Houdbaar eten': 'Shelf-stable food',
        'Persoonlijke medicatie': 'Personal medication',
        'Baby, huisdier en persoonlijke behoeften': 'Baby, pet and personal needs',
        'Hygiene en sanitatie': 'Hygiene and sanitation',
        'Documenten, geld en sleutels': 'Documents, money and keys',
        'Contactgegevens en noodplan': 'Contact details and emergency plan',
        'Informatie ontvangen': 'Receiving information',
        'Telefoon en kleine apparaten laden': 'Charging phones and small devices',
        'Verlichting': 'Lighting',
        'Water opslaan en meenemen': 'Storing and carrying water',
        'Water aanvullend filteren': 'Additional water filtration',
        'Eerste hulp': 'First aid',
        'Aandacht en noodsignalering': 'Attention and emergency signalling',
        'Bescherming tegen weer': 'Weather protection',
        'Eten kunnen bereiden zonder netstroom': 'Preparing food without mains power',
        'Gereedschap en kleine reparaties': 'Tools and minor repairs',
        'Navigeren zonder internet': 'Navigating without internet',
        'Spullen draagbaar opbergen': 'Portable storage',
        'Voertuig en pechvoorbereiding': 'Vehicle and breakdown preparation',
        'Sterke basis': 'Strong foundation',
        'Compact alternatief': 'Compact alternative',
        'Meer accu': 'More battery capacity',
        'Beste waarde': 'Best value',
        'Meer reserve': 'More backup capacity',
        'Compacte basis': 'Compact foundation',
        'USB-C alternatief': 'USB-C alternative',
        'Thuis en kamp': 'Home and camp',
        'Groep en kamp': 'Group and camp',
        'Persoonlijk': 'Personal',
        'Basis EHBO': 'First aid foundation',
        'Thermisch compact': 'Compact thermal protection',
        'Slimme add-on': 'Smart add-on',
        'Veel functies': 'Multi-function',
        'Compact draagbaar': 'Compact and portable',
        'Meer ruimte': 'More space',
        'Buiten bereiden': 'Outdoor cooking',
        'Auto basis': 'Car essentials',
        'Inhoud': 'Capacity',
        'Personen': 'People',
        'Gebruik': 'Use',
        'Afmetingen': 'Dimensions',
        'Gewicht': 'Weight',
        'Materiaal': 'Material',
        'Kleur': 'Colour',
        'Waterbestendigheid': 'Water resistance',
        'Compartimenten': 'Compartments',
        'Accucapaciteit': 'Battery capacity',
        'Specificatie': 'Specification'
      },
      de: {
        'PERSOONLIJKE KEUZEHULP': 'PERSÖNLICHER AUSWAHLHELFER',
        'Noodpakket samenstellen voor jouw situatie': 'Notfallpaket für Ihre Situation zusammenstellen',
        'Kies waarvoor je je wilt voorbereiden. We begeleiden je per onderdeel naar een passende selectie en laten duidelijk zien wat je zelf nog moet regelen.': 'Wählen Sie, worauf Sie sich vorbereiten möchten. Wir führen Sie für jeden Bedarf zu einer passenden Auswahl und zeigen klar, was Sie selbst noch organisieren müssen.',
        'Alleen kiezen wat je nog mist': 'Nur auswählen, was Ihnen noch fehlt',
        'Producten eenvoudig vergelijken': 'Produkte einfach vergleichen',
        'Alles overzichtelijk in één selectie': 'Alles übersichtlich in einer Auswahl',
        'COMBINEER & BESPAAR': 'KOMBINIEREN & SPAREN',
        'Bekijk hoe het bundelvoordeel werkt': 'So funktioniert der Staffelvorteil',
        'Kies je situatie': 'Wählen Sie Ihre Situation',
        '30 dagen retour': '30 Tage Rückgaberecht',
        'Gratis verzending vanaf €75': 'Kostenloser Versand ab €75',
        '72-uurs checklist openen': '72-Stunden-Checkliste öffnen',
        'Voortgang keuzehulp': 'Fortschritt des Auswahlhelfers',
        'Situatie': 'Situation',
        'Uitrusting': 'Ausrüstung',
        'Winkelwagen': 'Warenkorb',
        'Stap 1 van 3': 'Schritt 1 von 3',
        'Kies je situatie(s)': 'Wählen Sie Ihre Situation(en)',
        'Waarvoor wil je je voorbereiden? Je kunt meerdere situaties aanklikken. Gedeelde onderdelen worden samengevoegd, zodat je niets dubbel kiest.': 'Worauf möchten Sie sich vorbereiten? Sie können mehrere Situationen auswählen. Gemeinsame Bestandteile werden zusammengeführt, damit Sie nichts doppelt auswählen.',
        'Voor wie stel je dit samen?': 'Für wen stellen Sie dies zusammen?',
        'Eén korte vraag. Daarna zie je in één overzicht wat bij jouw situatie past.': 'Eine kurze Frage. Danach sehen Sie auf einen Blick, was zu Ihrer Situation passt.',
        'Voor hoeveel personen?': 'Für wie viele Personen?',
        "Dit bepaalt de aantallen in je pakket — drinkwater, warmte en EHBO — en welke extra's zinvol zijn.": 'Dies bestimmt die Mengen in Ihrem Paket – Trinkwasser, Wärme und Erste Hilfe – sowie sinnvolle Extras.',
        '1 persoon': '1 Person',
        '2 personen': '2 Personen',
        '3 personen': '3 Personen',
        '4 personen': '4 Personen',
        '5 of meer': '5 oder mehr',
        'Bij 72 uur is dit 9 liter drinkwater voor 1 persoon.': 'Für 72 Stunden sind das 9 Liter Trinkwasser für 1 Person.',
        'Hoe lang blijf je buiten zonder stroom?': 'Wie lange bleiben Sie ohne Strom im Freien?',
        'Dagtocht': 'Tagesausflug',
        'Overnachting': 'Übernachtung',
        'Meerdere dagen': 'Mehrere Tage',
        'Bij langere duur verschuift het advies naar extra energie, waterfiltering en warmte.': 'Bei längerer Dauer liegt der Schwerpunkt auf zusätzlicher Energie, Wasserfilterung und Wärme.',
        'Verder naar uitrusting': 'Weiter zur Ausrüstung',
        'Stap 2 van 3 · Jouw aanbevelingen': 'Schritt 2 von 3 · Ihre Empfehlungen',
        'Jouw aanbevelingen': 'Ihre Empfehlungen',
        'HANDIGE EXTRA’S': 'NÜTZLICHE EXTRAS',
        'MAAK JE UITRUSTING NOG PRAKTISCHER': 'MACHEN SIE IHRE AUSRÜSTUNG NOCH PRAKTISCHER',
        'Deze producten zijn handig, maar niet essentieel voor je basisadvies.': 'Diese Produkte sind nützlich, aber für Ihre Grundausstattung nicht unverzichtbar.',
        'Dit regel je zelf': 'Das organisieren Sie selbst',
        'Denk daarnaast aan drinkwater, eten, medicatie, persoonlijke benodigdheden en belangrijke documenten.': 'Denken Sie außerdem an Trinkwasser, Lebensmittel, Medikamente, persönliche Dinge und wichtige Dokumente.',
        'Bekijk de volledige 72-uurs checklist →': 'Vollständige 72-Stunden-Checkliste ansehen →',
        'Bekijk mijn selectie': 'Meine Auswahl prüfen',
        'Opnieuw beginnen': 'Neu beginnen',
        'Stap 3 van 3 · Winkelwagen': 'Schritt 3 von 3 · Warenkorb',
        'Jouw selectie': 'Ihre Auswahl',
        'Controleer aantallen en varianten. Daarna ga je veilig naar afrekenen.': 'Prüfen Sie Mengen und Varianten. Danach gehen Sie sicher zur Kasse.',
        'Je selectie is nog leeg.': 'Ihre Auswahl ist noch leer.',
        'Terug naar uitrusting': 'Zurück zur Ausrüstung',
        'In je pakket': 'In Ihrem Paket',
        '0 producten': '0 Produkte',
        'Subtotaal producten': 'Produktzwischensumme',
        'Veilig afrekenen': 'Sicher zur Kasse',
        'Naar checkout': 'Zur Kasse',
        'Uitrusting aanpassen': 'Ausrüstung anpassen',
        'Zekerheden bij bestellen': 'Sicherheiten bei der Bestellung',
        'Veilig betalen': 'Sicher bezahlen',
        'Dit koop je niet bij ons': 'Nicht bei uns erhältlich',
        'Wat je zelf nog regelt': 'Was Sie noch selbst organisieren',
        'Je bestelling dekt de uitrusting. Deze punten horen bij je voorbereiding maar verkopen we niet; regel ze zelf na je bestelling.': 'Ihre Bestellung umfasst die Ausrüstung. Diese Punkte gehören zu Ihrer Vorbereitung, werden von uns aber nicht verkauft; organisieren Sie sie nach Ihrer Bestellung selbst.',
        'Nog geen situatie gekozen': 'Noch keine Situation ausgewählt',
        'Kies een situatie om te beginnen.': 'Wählen Sie zunächst eine Situation.',
        'Bekijk alle gekozen producten': 'Alle ausgewählten Produkte ansehen',
        'Bekijk selectie': 'Auswahl ansehen',
        'Gekozen producten': 'Ausgewählte Produkte',
        'Sluit selectie': 'Auswahl schließen',
        'Selectie controleren': 'Auswahl prüfen',
        'Sluit productdetails': 'Produktdetails schließen',
        'Productdetails': 'Produktdetails',
        'Ander model kiezen': 'Anderes Modell wählen',
        'Kies een ander model': 'Wählen Sie ein anderes Modell',
        'Sluit modelkeuze': 'Modellauswahl schließen',
        'Sluit kortingsstaffel': 'Rabattstaffel schließen',
        'Zo werkt het staffelvoordeel': 'So funktioniert der Staffelvorteil',
        'Kwalificerende artikelen tellen per stuk mee. Producten die al in je winkelwagen staan, kunnen ook meetellen. De definitieve korting wordt automatisch toegepast bij het afrekenen.': 'Qualifizierende Artikel zählen pro Einheit. Qualifizierende Produkte, die bereits im Warenkorb liegen, können ebenfalls mitzählen. Der endgültige Rabatt wird beim Bezahlen automatisch angewendet.',
        '72 uur thuis voorbereid': '72 Stunden zu Hause vorbereitet',
        '72 uur thuis': '72 Stunden zu Hause',
        'Voor wanneer stroom, water, internet of winkels tijdelijk niet beschikbaar zijn.': 'Für vorübergehende Ausfälle von Strom, Wasser, Internet oder Einkaufsmöglichkeiten.',
        'Thuis': 'Zuhause',
        'Stroomuitval': 'Stromausfall',
        'Licht, informatie en bereik behouden wanneer de elektriciteit uitvalt.': 'Licht, Informationen und Erreichbarkeit bei einem Stromausfall erhalten.',
        'Energie': 'Energie',
        'Waterstoring': 'Wasserausfall',
        'Drinkwater valt uit': 'Trinkwasserausfall',
        'Water kunnen opslaan en alternatieven hebben wanneer kraanwater tijdelijk niet beschikbaar is.': 'Wasser lagern und Alternativen bereithalten, wenn Leitungswasser vorübergehend nicht verfügbar ist.',
        'Drinkwater zelf regelen en uitrusting kiezen voor opslag, transport en aanvullende filtering.': 'Trinkwasser selbst organisieren und Ausrüstung für Lagerung, Transport und zusätzliche Filterung wählen.',
        'Water': 'Wasser',
        'Snel moeten vertrekken': 'Schnell aufbrechen müssen',
        'Belangrijke spullen draagbaar en direct bereikbaar bij elkaar houden.': 'Wichtige Dinge tragbar, zusammen und sofort griffbereit halten.',
        'Vertrek': 'Aufbruch',
        'Auto en onderweg': 'Auto und unterwegs',
        'Auto & onderweg': 'Auto & unterwegs',
        'Voor pech, vertraging of onverwacht langer onderweg zijn.': 'Für Pannen, Verzögerungen oder unerwartet lange Fahrten.',
        'Voor pech, vertraging of onverwacht langere tijd onderweg zijn.': 'Für Pannen, Verzögerungen oder unerwartet lange Fahrten.',
        'Onderweg': 'Unterwegs',
        'Buiten zonder stroom': 'Draußen ohne Strom',
        'Compacte uitrusting voor kamperen, wandelen en buiten verblijven.': 'Kompakte Ausrüstung zum Campen, Wandern und für Aufenthalte im Freien.',
        'Verwarming valt uit': 'Heizungsausfall',
        'Warm en voorbereid blijven wanneer verwarming of elektriciteit langere tijd uitvalt.': 'Warm und vorbereitet bleiben, wenn Heizung oder Strom länger ausfallen.',
        'Warmte': 'Wärme',
        'Noodradio en informatie': 'Notfallradio und Informationen',
        'Energie & licht': 'Energie & Licht',
        'Stroom en licht die het doen zonder stopcontact.': 'Energie und Licht, die ohne Steckdose funktionieren.',
        'Water & filters': 'Wasser & Filter',
        'Filteren, bewaren en meenemen — van filterfles tot jerrycan. Gevuld drinkwater regel je zelf.': 'Wasser filtern, lagern und transportieren – von der Filterflasche bis zum Kanister. Gefülltes Trinkwasser organisieren Sie selbst.',
        'EHBO & veiligheid': 'Erste Hilfe & Sicherheit',
        'Eerste hulp, signalering en zichtbaarheid voor thuis, onderweg en buiten.': 'Erste Hilfe, Signalisierung und Sichtbarkeit zu Hause, unterwegs und im Freien.',
        'Kamperen & outdoor': 'Camping & Outdoor',
        'Slapen, koken en comfort voor langere tijd buiten.': 'Schlafen, Kochen und Komfort für längere Aufenthalte im Freien.',
        'Gereedschap & survival tools': 'Werkzeug & Survival-Ausrüstung',
        'Praktisch gereedschap dat je dagelijks of onderweg bij je hebt.': 'Praktisches Werkzeug für den Alltag und unterwegs.',
        'Tassen & onderweg': 'Taschen & unterwegs',
        'Maak je selectie draagbaar en geschikt voor auto, vertrek of vertraging.': 'Machen Sie Ihre Auswahl tragbar und geeignet für Auto, Aufbruch oder Verzögerungen.',
        'Bekijk alles voor onderweg': 'Gesamte Ausrüstung für unterwegs ansehen',
        'Radio-ontvangst en noodinformatie als internet of stroom wegvalt.': 'Radioempfang und Notfallinformationen, wenn Internet oder Strom ausfallen.',
        'Powerbank voor telefoon': 'Powerbank für das Telefon',
        'Reserve-energie voor telefoon en kleine USB-apparaten. Kabels controleer je apart.': 'Reserveenergie für Telefon und kleine USB-Geräte. Kabel prüfen Sie separat.',
        'Zaklamp of hoofdlamp': 'Taschenlampe oder Stirnlampe',
        'Losse verlichting zonder stopcontact.': 'Mobile Beleuchtung ohne Steckdose.',
        'Wateropslag en meenemen': 'Wasser lagern und transportieren',
        'Waterzakken en flessen helpen bij opslag of transport. Vullen blijft zelf regelen.': 'Wasserbeutel und Flaschen helfen bei Lagerung und Transport. Das Befüllen organisieren Sie selbst.',
        'Aanvullende waterfiltering': 'Zusätzliche Wasserfilterung',
        'Een filter kan handig zijn bij geschikte bronnen, maar vervangt geen drinkwatervoorraad.': 'Ein Filter kann bei geeigneten Quellen nützlich sein, ersetzt aber keinen Trinkwasservorrat.',
        'EHBO-set': 'Erste-Hilfe-Set',
        'Een gecontroleerde EHBO-basis voor kleine verwondingen en onverwachte situaties.': 'Eine geprüfte Erste-Hilfe-Grundausstattung für kleinere Verletzungen und unerwartete Situationen.',
        'Noodsignalering': 'Notsignalisierung',
        'Maak jezelf hoorbaar of vindbaar.': 'Machen Sie sich hörbar oder auffindbar.',
        'Warm blijven': 'Warm bleiben',
        'Thermische noodwarmte naast eigen kleding en dekens.': 'Thermische Notfallwärme zusätzlich zu eigener Kleidung und Decken.',
        'Bereiden zonder netstroom': 'Zubereitung ohne Netzstrom',
        'Uitrusting om buiten of veilig geventileerd te bereiden. Eten en brandstof controleer je zelf.': 'Ausrüstung zur Zubereitung im Freien oder in sicher belüfteten Bereichen. Lebensmittel und Brennstoff organisieren Sie selbst.',
        'Multitool en kleine reparaties': 'Multitool und kleine Reparaturen',
        'Voor openen, vastzetten en eenvoudige noodoplossingen.': 'Zum Öffnen, Befestigen und für einfache Notreparaturen.',
        'Navigatie zonder internet': 'Navigation ohne Internet',
        'Een offline route, kaart of kompas naast je telefoon.': 'Eine Offline-Route, Karte oder ein Kompass zusätzlich zum Telefon.',
        'Tas of draagset': 'Tasche oder Trageset',
        'Houd de gekozen uitrusting draagbaar bij elkaar.': 'Halten Sie die gewählte Ausrüstung zusammen und tragbar.',
        'Auto en pechvoorbereiding': 'Auto- und Pannenvorsorge',
        'Gerichte uitrusting voor pech, accu, banden en vertraging.': 'Gezielte Ausrüstung für Pannen, Batterie, Reifen und Verzögerungen.',
        'Handige extra': 'Nützliches Extra',
        'Voor extra zekerheid': 'Für zusätzliche Sicherheit',
        'Comfort onderweg': 'Komfort unterwegs',
        'Opgeslagen drinkwater': 'Gelagertes Trinkwasser',
        'Houdbaar eten': 'Haltbare Lebensmittel',
        'Persoonlijke medicatie': 'Persönliche Medikamente',
        'Baby, huisdier en persoonlijke behoeften': 'Baby, Haustier und persönliche Bedürfnisse',
        'Hygiene en sanitatie': 'Hygiene und Sanitärversorgung',
        'Documenten, geld en sleutels': 'Dokumente, Geld und Schlüssel',
        'Contactgegevens en noodplan': 'Kontaktdaten und Notfallplan',
        'Informatie ontvangen': 'Informationen empfangen',
        'Telefoon en kleine apparaten laden': 'Telefon und kleine Geräte laden',
        'Verlichting': 'Beleuchtung',
        'Water opslaan en meenemen': 'Wasser lagern und mitnehmen',
        'Water aanvullend filteren': 'Wasser zusätzlich filtern',
        'Eerste hulp': 'Erste Hilfe',
        'Aandacht en noodsignalering': 'Aufmerksamkeit und Notsignalisierung',
        'Bescherming tegen weer': 'Wetterschutz',
        'Eten kunnen bereiden zonder netstroom': 'Essen ohne Netzstrom zubereiten',
        'Gereedschap en kleine reparaties': 'Werkzeug und kleine Reparaturen',
        'Navigeren zonder internet': 'Ohne Internet navigieren',
        'Spullen draagbaar opbergen': 'Ausrüstung tragbar verstauen',
        'Voertuig en pechvoorbereiding': 'Fahrzeug- und Pannenvorsorge',
        'Sterke basis': 'Starke Basis',
        'Compact alternatief': 'Kompakte Alternative',
        'Meer accu': 'Mehr Akkukapazität',
        'Beste waarde': 'Bestes Preis-Leistungs-Verhältnis',
        'Meer reserve': 'Mehr Reserve',
        'Compacte basis': 'Kompakte Basis',
        'USB-C alternatief': 'USB-C-Alternative',
        'Thuis en kamp': 'Zuhause und Camp',
        'Groep en kamp': 'Gruppe und Camp',
        'Persoonlijk': 'Persönlich',
        'Basis EHBO': 'Erste-Hilfe-Basis',
        'Thermisch compact': 'Kompakter Wärmeschutz',
        'Slimme add-on': 'Sinnvolle Ergänzung',
        'Veel functies': 'Viele Funktionen',
        'Compact draagbaar': 'Kompakt und tragbar',
        'Meer ruimte': 'Mehr Platz',
        'Buiten bereiden': 'Kochen im Freien',
        'Auto basis': 'Auto-Grundausstattung',
        'Inhoud': 'Kapazität',
        'Personen': 'Personen',
        'Gebruik': 'Verwendung',
        'Afmetingen': 'Abmessungen',
        'Gewicht': 'Gewicht',
        'Materiaal': 'Material',
        'Kleur': 'Farbe',
        'Waterbestendigheid': 'Wasserbeständigkeit',
        'Compartimenten': 'Fächer',
        'Accucapaciteit': 'Akkukapazität',
        'Specificatie': 'Spezifikation'
      }
    };
    const exactCopy = exactTranslations[language] || {};
    const translateExact = (value) => exactCopy[String(value || '').trim()] || value;
    const translateConfig = (value) => {
      if (Array.isArray(value)) return value.map(translateConfig);
      if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [translateExact(key), translateConfig(item)]));
      }
      return typeof value === 'string' ? translateExact(value) : value;
    };
    if (language === 'en' || language === 'de') {
      config = translateConfig(config);
      const genericRequirement = language === 'de'
        ? 'Prüfen und organisieren Sie diesen Punkt passend zu den Bedürfnissen Ihres Haushalts.'
        : 'Check and arrange this item according to your household’s needs.';
      (config.requirements || []).forEach((requirement) => { requirement.description = genericRequirement; });
    }

    const translateStaticDom = () => {
      if (language !== 'en' && language !== 'de') return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const original = node.nodeValue;
        const trimmed = original.trim();
        if (!trimmed || !exactCopy[trimmed]) return;
        node.nodeValue = original.replace(trimmed, exactCopy[trimmed]);
      });
      root.querySelectorAll('[aria-label], [title], [placeholder]').forEach((node) => {
        ['aria-label', 'title', 'placeholder'].forEach((name) => {
          const value = node.getAttribute(name);
          if (value && exactCopy[value]) node.setAttribute(name, exactCopy[value]);
        });
      });
    };
    translateStaticDom();
    const localizedLongBlocks = {
      en: {
        support: `<p class="asb__eyebrow">About this selection guide</p><h2>Build equipment for each situation</h2><p>This guide works in three steps. First choose your situation, then review the products that match it. For each product you see the need it covers, why it fits and the current price. Add only what you need or choose another model for the same need. In the final step you review variants and quantities before checkout. Items you need to arrange yourself are listed separately.</p><h3>Build a 72-hour home emergency kit</h3><p>Preparedness at home focuses on lighting, information, charging, first aid and water storage. Arrange drinking water, shelf-stable food and personal medication yourself.</p><h3>Preparing for a power outage</h3><p>Without power, lighting, internet access and phone charging are affected first. The guide prioritises an emergency radio, lighting and a power bank.</p><h3>Equipment for the car and travel</h3><p>For breakdowns or delays, keep first aid, lighting, warmth, signalling and targeted vehicle assistance in your car.</p><h3>Water storage and additional filtration</h3><p>A filter, bottle or empty water bag is equipment, not a water supply. Stored drinking water remains essential.</p><h3>Power, lighting and communication</h3><p>Power banks, torches, headlamps and emergency radios help you remain reachable and informed. Check the required cable or battery for each device.</p><h3>First aid and safety</h3><p>A checked first aid kit and simple signalling equipment belong in every situation. Arrange personal medication yourself.</p><h3>What you arrange yourself</h3><p>Alfa Survival does not sell filled drinking water, food, medication, baby or pet products, loose batteries or supermarket goods. The guide lists these items so your preparations remain complete.</p>`,
        faqTitle: 'Frequently asked questions',
        faq: `<h3 class="asb__faq-group-head">About the selection guide <span>5 questions</span></h3><details><summary>How does the selection guide work?</summary><p>Choose one or more situations first. You will then see the products that suit those situations. Add what you need or choose another model. In step 3, review your complete selection, variants and quantities before checkout.</p></details><details><summary>Why are drinking water and food listed as items to arrange yourself?</summary><p>Quantity, shelf life, diet and personal circumstances differ for each household. The guide shows what you need without linking these items to a product.</p></details><details><summary>Does a water filter replace stored drinking water?</summary><p>No. A water filter is an addition for specific sources and situations and only works within the product’s proven limits.</p></details><details><summary>Can I combine multiple situations?</summary><p>Yes. Select several situations in step 1. Products shared by multiple situations are combined so you do not choose anything twice.</p></details><details><summary>Is my selection saved?</summary><p>Yes. Your progress is saved in your browser, including when you leave or refresh the page. Returning to an earlier step does not clear it.</p></details>`
      },
      de: {
        support: `<p class="asb__eyebrow">Über diesen Auswahlhelfer</p><h2>Ausrüstung passend zur Situation zusammenstellen</h2><p>Dieser Auswahlhelfer funktioniert in drei Schritten. Wählen Sie zuerst Ihre Situation und prüfen Sie anschließend die passenden Produkte. Zu jedem Produkt sehen Sie den Bedarf, warum es passt und den aktuellen Preis. Fügen Sie nur hinzu, was Sie benötigen, oder wählen Sie ein anderes Modell. Im letzten Schritt prüfen Sie Varianten und Mengen vor dem Bezahlen. Punkte, die Sie selbst organisieren, werden separat aufgeführt.</p><h3>Notfallpaket für 72 Stunden zu Hause</h3><p>Für die Vorbereitung zu Hause zählen Beleuchtung, Informationen, Lademöglichkeiten, Erste Hilfe und Wasserlagerung. Trinkwasser, haltbare Lebensmittel und persönliche Medikamente organisieren Sie selbst.</p><h3>Vorbereitung auf einen Stromausfall</h3><p>Ohne Strom fallen Beleuchtung, Internet und das Laden des Telefons zuerst aus. Der Auswahlhelfer priorisiert Notfallradio, Beleuchtung und Powerbank.</p><h3>Ausrüstung für Auto und unterwegs</h3><p>Bei Pannen oder Verzögerungen gehören Erste Hilfe, Licht, Wärme, Signalisierung und gezielte Fahrzeughilfe ins Auto.</p><h3>Wasserlagerung und zusätzliche Filterung</h3><p>Ein Filter, eine Flasche oder ein leerer Wasserbeutel ist Ausrüstung, kein Wasservorrat. Gelagertes Trinkwasser bleibt unverzichtbar.</p><h3>Energie, Beleuchtung und Kommunikation</h3><p>Powerbanks, Taschenlampen, Stirnlampen und Notfallradios helfen Ihnen, erreichbar und informiert zu bleiben. Prüfen Sie für jedes Gerät die benötigten Kabel oder Batterien.</p><h3>Erste Hilfe und Sicherheit</h3><p>Ein geprüftes Erste-Hilfe-Set und einfache Signalmittel gehören in jede Situation. Persönliche Medikamente organisieren Sie selbst.</p><h3>Was Sie selbst organisieren</h3><p>Alfa Survival verkauft kein abgefülltes Trinkwasser, keine Lebensmittel, Medikamente, Baby- oder Haustierprodukte, losen Batterien oder Supermarktartikel. Der Auswahlhelfer führt diese Punkte auf, damit Ihre Vorbereitung vollständig bleibt.</p>`,
        faqTitle: 'Häufig gestellte Fragen',
        faq: `<h3 class="asb__faq-group-head">Über den Auswahlhelfer <span>5 Fragen</span></h3><details><summary>Wie funktioniert der Auswahlhelfer?</summary><p>Wählen Sie zuerst eine oder mehrere Situationen. Danach sehen Sie die passenden Produkte. Fügen Sie hinzu, was Sie benötigen, oder wählen Sie ein anderes Modell. In Schritt 3 prüfen Sie Ihre vollständige Auswahl, Varianten und Mengen vor dem Bezahlen.</p></details><details><summary>Warum sind Trinkwasser und Lebensmittel als selbst zu organisieren aufgeführt?</summary><p>Menge, Haltbarkeit, Ernährung und persönliche Umstände unterscheiden sich je Haushalt. Der Auswahlhelfer zeigt den Bedarf, ohne ihn mit einem Produkt zu verknüpfen.</p></details><details><summary>Ersetzt ein Wasserfilter meinen Trinkwasservorrat?</summary><p>Nein. Ein Wasserfilter ist eine Ergänzung für bestimmte Quellen und Situationen und funktioniert nur innerhalb der nachgewiesenen Produktgrenzen.</p></details><details><summary>Kann ich mehrere Situationen kombinieren?</summary><p>Ja. Wählen Sie in Schritt 1 mehrere Situationen. Produkte, die zu mehreren Situationen passen, werden zusammengeführt.</p></details><details><summary>Wird meine Auswahl gespeichert?</summary><p>Ja. Ihr Fortschritt bleibt im Browser gespeichert, auch wenn Sie die Seite verlassen oder aktualisieren. Das Zurückgehen zu einem früheren Schritt löscht nichts.</p></details>`
      }
    };
    const longCopy = localizedLongBlocks[language];
    if (longCopy) {
      const support = root.querySelector('.asb__support-copy');
      if (support) support.innerHTML = longCopy.support;
      const faqTitle = root.querySelector('[id^="asb-faq-title-"]');
      if (faqTitle) faqTitle.textContent = longCopy.faqTitle;
      const faqGroup = root.querySelector('.asb__faq-group');
      if (faqGroup) faqGroup.innerHTML = longCopy.faq;
    }

    const sectionId = root.dataset.sectionId || 'page';
    const storageKey = `alfa-situatiekeuzehulp-phase-d-${sectionId}`;
    const oldStorageKey = `alfa-situatiekeuzehulp-phase-c-${sectionId}`;
    const isDesignMode = () => Boolean(window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
    const clearLegacyStoredState = () => {
      if (isDesignMode()) return;
      try {
        window.localStorage.removeItem(storageKey);
        window.localStorage.removeItem(oldStorageKey);
      } catch (error) {
      }
    };

    const situationList = (config.situations || [])
      .filter((item) => item.active !== false)
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    const situations = new Map(situationList.map((item) => [item.id, item]));
    const requirementList = (config.requirements || []).map((item, index) => ({ ...item, sort_order: index }));
    const requirements = new Map(requirementList.map((item) => [item.id, item]));
    const categoryList = (config.categories || []).map((item, index) => ({ ...item, sort_order: index }));
    const categories = new Map(categoryList.map((item) => [item.id, item]));
    const themeList = (config.themes || []).slice().sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    const themes = new Map(themeList.map((item) => [item.id, item]));
    const levels = new Map((config.levels || []).map((item) => [item.id, item]));
    const durationOptions = config.duration_options || [];
    const normalizeSpecs = (specs) => Object.entries(specs || {}).reduce((normalized, [key, value]) => {
      const cleanKey = String(key || '').trim();
      const cleanValue = String(value || '').trim();
      if (cleanKey && cleanValue && cleanValue !== 'null') normalized[cleanKey] = cleanValue;
      return normalized;
    }, {});
    const normalizeVariant = (variant) => ({
      id: variant?.id ? Number(variant.id) : null,
      title: variant?.title || null,
      available: Boolean(variant?.available && variant?.id),
      price_cents: Number(variant?.price_cents || 0),
      compare_at_price_cents: Number(variant?.compare_at_price_cents || 0),
      image: variant?.image || '',
      image_srcset: variant?.image_srcset || '',
      image_alt: variant?.image_alt || '',
      image_width: Number(variant?.image_width || 0),
      image_height: Number(variant?.image_height || 0),
      inventory: variant?.inventory === null || variant?.inventory === undefined ? null : Number(variant.inventory),
      inventory_tracked: Boolean(variant?.inventory_tracked),
    });
    const productChoices = new Map(Object.entries(config.product_choices || {}).map(([categoryId, choices]) => [
      categoryId,
      (Array.isArray(choices) ? choices : [])
        .map((choice) => {
          const localizedCategory = (config.categories || []).find((category) => category.id === categoryId);
          const variants = (Array.isArray(choice.variants) ? choice.variants : [])
            .map(normalizeVariant)
            .filter((variant) => variant.id);
          const fallbackVariant = normalizeVariant({
            id: choice.variant_id,
            title: choice.variant_title,
            available: choice.available,
            price_cents: choice.price_cents,
            compare_at_price_cents: choice.compare_at_price_cents,
            image: choice.image,
            image_srcset: choice.image_srcset,
            image_alt: choice.image_alt,
            image_width: choice.image_width,
            image_height: choice.image_height,
            inventory: choice.inventory,
            inventory_tracked: choice.inventory_tracked,
          });
          const defaultVariant = variants.find((variant) => variant.id === fallbackVariant.id)
            || variants.find((variant) => variant.available)
            || fallbackVariant;

          return {
            ...choice,
            display_title: language === 'nl' ? choice.display_title : (choice.title || choice.display_title),
            reason: language === 'nl' ? choice.reason : (localizedCategory?.summary || choice.reason),
            sales_note: language === 'nl' ? choice.sales_note : (localizedCategory?.summary || choice.sales_note),
            criteria: language === 'nl' ? choice.criteria : [],
            variants,
            specs: normalizeSpecs(choice.specs),
            variant_id: defaultVariant.id,
            variant_title: defaultVariant.title,
            price_cents: Number(defaultVariant.price_cents || 0),
            compare_at_price_cents: Number(defaultVariant.compare_at_price_cents || 0),
            image: defaultVariant.image || choice.image || '',
            image_srcset: defaultVariant.image_srcset || choice.image_srcset || '',
            image_alt: defaultVariant.image_alt || choice.image_alt || choice.display_title || choice.title || '',
            image_width: Number(defaultVariant.image_width || choice.image_width || 0),
            image_height: Number(defaultVariant.image_height || choice.image_height || 0),
            inventory: defaultVariant.inventory,
            inventory_tracked: Boolean(defaultVariant.inventory_tracked),
            rating: Number(choice.rating || 0),
            rating_count: Number(choice.rating_count || 0),
            available: Boolean(choice.available && defaultVariant.available && defaultVariant.id),
          };
        })
        .filter((choice) => choice.available && choice.variant_id),
    ]));

    const routeRoot = window.Shopify?.routes?.root || '/';
    const normalizedRouteRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    const cartUrl = `${normalizedRouteRoot}cart`;
    const cartAddUrl = `${normalizedRouteRoot}cart/add.js`;
    const sales = config.sales || {};
    const discountPolicy = config.discount_policy || {};
    const freeShippingThresholdCents = Math.max(0, Number(sales.free_shipping_threshold_cents || 0));
    const bundleDiscountTiers = (Array.isArray(discountPolicy.tiers) ? discountPolicy.tiers : [])
      .map((tier) => ({
        min: Number(tier.min_products || tier.min || 0),
        percentage: Number(tier.percentage || 0),
      }))
      .filter((tier) => tier.min > 0 && tier.percentage > 0)
      .sort((a, b) => a.min - b.min);
    const configuredMaxDiscountPercentage = Number(discountPolicy.max_percentage || 0);
    const maxBundleDiscountPercentage = configuredMaxDiscountPercentage > 0
      ? configuredMaxDiscountPercentage
      : bundleDiscountTiers.reduce((max, tier) => Math.max(max, Number(tier.percentage || 0)), 0);

    const productDiscountEligible = (product) => Boolean(product?.discount_qualifies === true);

    const state = {
      view: 'situations',
      selectedSituationIds: [],
      people: '1',
      duration: 'dagtocht',
      categoryStatus: {},
      choiceByCategory: {},
      variantByChoice: {},
      qtyByCategory: {},
      addPanelOpen: false,
      tiersOpen: false,
      comparePanel: '',
      quickviewPanel: '',
      removedCategories: {},
      showAllLines: false,
      lastAddedSignature: '',
    };

    let cartBusy = false;
    let activeLayer = null;
    let lastFocusedElement = null;

    const views = {
      situations: root.querySelector('[data-view="situations"]'),
      result: root.querySelector('[data-view="result"]'),
      cart: root.querySelector('[data-view="cart"]'),
    };
    const summaryAside = root.querySelector('.asb__summary');
    const shellNode = root.querySelector('.asb__shell');
    const cartLinesNode = root.querySelector('[data-cart-lines]');
    const cartEmptyNode = root.querySelector('[data-cart-empty]');
    const stepBar = root.querySelector('[data-step-bar]');

    const liveRegion = root.querySelector('[data-live-region]');
    const addPanel = root.querySelector('[data-add-situation-panel]');
    const addGrid = root.querySelector('[data-add-situation-grid]');
    const selectedSummary = root.querySelector('[data-selected-summary]');
    const durationField = root.querySelector('[data-duration-field]');
    const durationNote = root.querySelector('[data-duration-note]');
    const questionContext = root.querySelector('[data-question-context]');
    const resultContext = root.querySelector('[data-result-context]');
    const waterHint = root.querySelector('[data-water-hint]');
    const recoTitle = root.querySelector('[data-reco-title]');
    const recoSub = root.querySelector('[data-reco-sub]');
    const recoMeta = root.querySelector('[data-reco-meta]');
    const recoMediaNode = root.querySelector('[data-reco-media]');
    const recoNotice = root.querySelector('[data-reco-notice]');
    const recoGrid = root.querySelector('[data-reco-grid]');
    const bonusBlock = root.querySelector('[data-bonus-block]');
    const bonusGrid = root.querySelector('[data-bonus-grid]');
    const comparePanelNode = root.querySelector('[data-compare-panel]');
    const comparePanelInner = root.querySelector('.asb__cmp-panel-inner');
    const compareBody = root.querySelector('[data-compare-body]');
    const compareTitleNode = root.querySelector('[data-compare-title]');
    const quickviewPanelNode = root.querySelector('[data-quickview-panel]');
    const quickviewPanelInner = root.querySelector('.asb__quickview-inner');
    const quickviewBody = root.querySelector('[data-quickview-body]');
    const quickviewTitleNode = root.querySelector('[data-quickview-title]');
    const summaryTitle = root.querySelector('[data-summary-title]');
    const summaryMeta = root.querySelector('[data-summary-meta]');
    const summaryNeeds = root.querySelector('[data-summary-needs]');
    const summarySelf = root.querySelector('[data-summary-self]');
    const summaryOpen = root.querySelector('[data-summary-open]');
    const summaryLines = root.querySelector('[data-summary-lines]');
    const summaryTotal = root.querySelector('[data-summary-total]');
    const summaryTotalOriginal = root.querySelector('[data-summary-total-original]');
    const summaryTotalFinal = root.querySelector('[data-summary-total-final]');
    const upsellNodes = root.querySelectorAll('[data-upsell]');
    const summarySavings = root.querySelector('[data-summary-savings]');
    const discountBars = root.querySelectorAll('[data-discount-bar]');
    const bundleBanners = root.querySelectorAll('[data-bundle-banner]');
    const discountFlags = root.querySelectorAll('[data-discount-flag]');
    const discountLabels = root.querySelectorAll('[data-discount-label]');
    const mobileFlag = root.querySelector('[data-mobile-flag]');
    const tierModal = root.querySelector('[data-tier-modal]');
    const tierRows = root.querySelector('[data-tier-rows]');
    const tierTitle = root.querySelector('[data-tier-title]');
    const tierExplanation = root.querySelector('[data-tier-explanation]');
    const heroBundle = root.querySelector('[data-hero-bundle]');
    const heroBundleTitle = root.querySelector('[data-hero-bundle-title]');
    const heroBundleText = root.querySelector('[data-hero-bundle-text]');
    const tierTriggers = root.querySelectorAll('[data-tier-trigger]');
    const discountNextNodes = root.querySelectorAll('[data-discount-next]');
    const cartStatus = root.querySelector('[data-cart-status]');
    const checkoutLinks = root.querySelectorAll('[data-checkout-link]');
    const progressMain = root.querySelector('[data-progress-main]');
    const progressDetail = root.querySelector('[data-progress-detail]');
    const progressMeter = root.querySelector('[data-progress-meter]');
    const selfSummary = root.querySelector('[data-self-summary]');
    const overviewBody = root.querySelector('[data-overview-body]');
    const wizardTitle = root.querySelector('[data-wizard-title]');
    const wizardMeta = root.querySelector('[data-wizard-meta]');
    const summaryMore = root.querySelector('[data-toggle-all-lines]');
    const summaryReviewButton = root.querySelector('[data-summary-review]');
    const summaryCartButton = root.querySelector('[data-summary-cart]');
    const addToggle = root.querySelector('[data-toggle-add-situation]');
    const totalNodes = root.querySelectorAll('[data-selection-total]');
    const selfCount = root.querySelector('[data-self-count]');
    const selfTeaser = root.querySelector('[data-self-teaser]');
    const selfProgress = root.querySelector('[data-self-progress]');
    const selfMeter = root.querySelector('[data-self-meter]');
    const countNodes = root.querySelectorAll('[data-selection-count]');
    const subtotalNodes = root.querySelectorAll('[data-selection-subtotal]');
    const productSubtotalLabels = root.querySelectorAll('[data-product-subtotal-label]');
    const discountRows = root.querySelectorAll('[data-discount-row]');
    const discountValues = root.querySelectorAll('[data-discount-value]');
    const mobileBar = root.querySelector('[data-open-selection-drawer]');
    const mobileCount = root.querySelector('[data-mobile-count]');
    const mobileTotal = root.querySelector('[data-mobile-total]');
    const drawer = root.querySelector('[data-selection-drawer]');
    const drawerPanel = root.querySelector('.asb__drawer-panel');
    const drawerLines = root.querySelector('[data-drawer-lines]');
    const drawerTotal = root.querySelector('[data-drawer-total]');
    const drawerTotalOriginal = root.querySelector('[data-drawer-total-original]');
    const drawerTotalFinal = root.querySelector('[data-drawer-total-final]');
    const drawerSavings = root.querySelector('[data-drawer-savings]');
    const shippingNodes = root.querySelectorAll('[data-shipping]');
    const shippingStatusNodes = root.querySelectorAll('[data-shipping-status]');
    const shippingMeterNodes = root.querySelectorAll('[data-shipping-meter]');
    const taxNoteNodes = root.querySelectorAll('[data-tax-note]');
    const selfBlocks = root.querySelectorAll('[data-self-block]');
    const seoLinksNode = root.querySelector('[data-seo-links]');

    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const announce = (message) => {
      if (liveRegion) liveRegion.textContent = message;
    };

    const formatMoney = (cents) => {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(sales.locale || 'nl-NL', {
          style: 'currency',
          currency: sales.currency || 'EUR',
        }).format(amount);
      } catch (error) {
        return `€${amount.toFixed(2).replace('.', ',')}`;
      }
    };

    const formatPercent = (value) => {
      try {
        return new Intl.NumberFormat(sales.locale || 'nl-NL', { maximumFractionDigits: 1 }).format(Number(value || 0));
      } catch (error) {
        return String(Number(value || 0)).replace('.', ',');
      }
    };
    const discountLanguage = String(sales.locale || 'nl').toLowerCase().split('-')[0];
    const discountTranslations = {
      nl: {
        overview: (maximum) => `Staffelvoordeel tot ${maximum}%`,
        expected: (percentage) => `Verwacht staffelvoordeel: ${percentage}%`,
        selected: (count) => `Je hebt ${count} kwalificerend${count === 1 ? '' : 'e'} artikel${count === 1 ? '' : 'en'} geselecteerd.`,
        next: (count, percentage) => `Nog ${count} kwalificerend${count === 1 ? '' : 'e'} artikel${count === 1 ? '' : 'en'} voor ${percentage}% verwacht staffelvoordeel.`,
        tierUnits: (count) => `${count} kwalificerend${count === 1 ? '' : 'e'} artikel${count === 1 ? '' : 'en'}`,
        cartNote: 'Producten die al in je winkelwagen staan, kunnen ook meetellen.',
        checkoutNote: 'De definitieve korting wordt automatisch toegepast bij het afrekenen.',
        hero: (maximum) => `Verwacht tot ${maximum}% staffelvoordeel`,
        heroText: 'Kies kwalificerende artikelen en bekijk welk staffelvoordeel je kunt verwachten. De definitieve korting wordt toegepast bij het afrekenen.',
        tierTitle: 'Zo werkt het staffelvoordeel',
        tierExplanation: 'Kwalificerende artikelen tellen per stuk mee. Producten die al in je winkelwagen staan, kunnen ook meetellen. De definitieve korting wordt automatisch toegepast bij het afrekenen.',
        mobileExpected: (percentage) => `${percentage}% verwacht`,
        productSubtotal: 'Subtotaal producten',
        shippingBelow: (amount, threshold) => `Nog ${amount} productsubtotaal tot ${threshold}. Verzendkosten worden bevestigd bij het afrekenen.`,
        shippingThreshold: (threshold) => `Selectie boven ${threshold} vóór korting. Verzendkosten worden bevestigd bij het afrekenen.`,
      },
      en: {
        overview: (maximum) => `Tiered savings up to ${maximum}%`,
        expected: (percentage) => `Expected tiered savings: ${percentage}%`,
        selected: (count) => `You have selected ${count} qualifying item${count === 1 ? '' : 's'}.`,
        next: (count, percentage) => `${count} more qualifying item${count === 1 ? '' : 's'} for an expected ${percentage}% tiered saving.`,
        tierUnits: (count) => `${count} qualifying item${count === 1 ? '' : 's'}`,
        cartNote: 'Qualifying products already in your cart may also count.',
        checkoutNote: 'The final discount is applied automatically at checkout.',
        hero: (maximum) => `Expect up to ${maximum}% tiered savings`,
        heroText: 'Select qualifying items to see the tiered saving you can expect. The final discount is applied at checkout.',
        tierTitle: 'How tiered savings work',
        tierExplanation: 'Qualifying items count per unit. Qualifying products already in your cart may also count. The final discount is applied automatically at checkout.',
        mobileExpected: (percentage) => `${percentage}% expected`,
        productSubtotal: 'Product subtotal',
        shippingBelow: (amount, threshold) => `${amount} more in product subtotal to ${threshold}. Shipping is confirmed at checkout.`,
        shippingThreshold: (threshold) => `Selection above ${threshold} before discounts. Shipping is confirmed at checkout.`,
      },
      de: {
        overview: (maximum) => `Staffelvorteil bis zu ${maximum}%`,
        expected: (percentage) => `Erwarteter Staffelvorteil: ${percentage}%`,
        selected: (count) => `Sie haben ${count} qualifizierende${count === 1 ? 'n Artikel' : ' Artikel'} ausgewählt.`,
        next: (count, percentage) => `Noch ${count} qualifizierende${count === 1 ? 'r Artikel' : ' Artikel'} für einen erwarteten Staffelvorteil von ${percentage}%.`,
        tierUnits: (count) => `${count} qualifizierende${count === 1 ? 'r Artikel' : ' Artikel'}`,
        cartNote: 'Qualifizierende Produkte im Warenkorb können ebenfalls mitzählen.',
        checkoutNote: 'Der endgültige Rabatt wird beim Bezahlen automatisch angewendet.',
        hero: (maximum) => `Bis zu ${maximum}% Staffelvorteil erwartet`,
        heroText: 'Wählen Sie qualifizierende Artikel aus und sehen Sie den erwarteten Staffelvorteil. Der endgültige Rabatt wird beim Bezahlen angewendet.',
        tierTitle: 'So funktioniert der Staffelvorteil',
        tierExplanation: 'Qualifizierende Artikel zählen pro Einheit. Qualifizierende Produkte im Warenkorb können ebenfalls mitzählen. Der endgültige Rabatt wird beim Bezahlen automatisch angewendet.',
        mobileExpected: (percentage) => `${percentage}% erwartet`,
        productSubtotal: 'Produktzwischensumme',
        shippingBelow: (amount, threshold) => `Noch ${amount} Produktzwischensumme bis ${threshold}. Versandkosten werden beim Bezahlen bestätigt.`,
        shippingThreshold: (threshold) => `Auswahl über ${threshold} vor Rabatten. Versandkosten werden beim Bezahlen bestätigt.`,
      },
    };
    const discountCopy = discountTranslations[discountLanguage] || discountTranslations.nl;
    const uiTranslations = {
      nl: {
        people: (count, fivePlus) => fivePlus ? '5+ personen' : `${count} ${count === '1' ? 'persoon' : 'personen'}`,
        water: (litres, fivePlus) => `${fivePlus ? 'minimaal ' : ''}${litres} liter`,
        waterStart: (amount) => `Voor jouw 72-uursbasis: ${amount} opgeslagen drinkwater. Een waterfilter is alleen een aanvulling.`,
        waterGeneral: 'Leg drinkwater klaar passend bij je situatie. De 72-uursberekening verschijnt zodra je 72 uur thuis voorbereid kiest.',
        fivePlusNote: '5+ is een startaantal; controleer per persoon.',
        suitableExtra: (category) => `Passende aanvulling: je hebt ${category.toLowerCase()} nog niet gekozen.`,
        quantityOf: (title) => `Aantal van ${title}`,
        compareModels: 'Vergelijk modellen', remove: 'Verwijder', decrease: 'Aantal verlagen', increase: 'Aantal verhogen',
        items: (count) => `${count} ${count === 1 ? 'artikel' : 'artikelen'}`,
        products: (count) => `${count} ${count === 1 ? 'product' : 'producten'}`,
        empty: 'Nog geen producten gekozen.<span>Kies hieronder per onderdeel wat je wilt toevoegen.</span>',
        showAll: (count) => `Bekijk alle ${count} gekozen producten`, noSituation: 'Nog geen situatie gekozen', chooseSituation: 'Kies een situatie om te beginnen.',
        selectionAdded: 'Selectie al toegevoegd', addProducts: (count) => `Voeg ${count} ${count === 1 ? 'product' : 'producten'} toe`, addSelection: 'Voeg mijn selectie toe',
        recommendationsFor: (situation) => `Jouw aanbevelingen voor ${situation}`, recommendations: 'Jouw aanbevelingen',
        recommendationSub: (count) => `Op basis van jouw situatie hebben we ${count} passende producten geselecteerd. Voeg alleen toe wat je nodig hebt.`,
        chooseFirst: 'Kies eerst een situatie. Daarna staan hier de producten die bij je passen.',
        recommendedProducts: (count) => `${count} ${count === 1 ? 'aanbevolen product' : 'aanbevolen producten'}`,
        editSituation: 'Wijzig situatie', unavailable: (count) => `${count} ${count === 1 ? 'aanbevolen product is' : 'aanbevolen producten zijn'} nu niet leverbaar en ${count === 1 ? 'is' : 'zijn'} daarom niet getoond.`,
        currentChoice: '✓ Jouw huidige keuze', selectedNow: '✓ Nu geselecteerd', chooseModel: (price) => `Kies dit model — ${price}`,
        noAlternative: 'Voor deze behoefte is nu geen alternatief beschikbaar.', compareProducts: 'Producten vergelijken', compareOther: 'Vergelijk andere modellen',
        compareIntro: 'Vergelijk de belangrijkste verschillen en kies wat het beste bij jouw noodpakket past.', unavailableProduct: 'Dit product is nu niet beschikbaar.',
        variant: 'Variant', variantOrColor: 'Variant of kleur', quantity: 'Aantal', adding: 'Toevoegen...', checkout: 'Veilig afrekenen',
        alreadyAdded: 'Deze selectie is al toegevoegd.', viewCart: 'Bekijk winkelwagen', addingSelection: 'Bezig met toevoegen...', added: 'Toegevoegd aan je winkelwagen.', checkoutDirect: 'of reken direct af.',
        productsAdded: (count) => `${count} ${count === 1 ? 'product is' : 'producten zijn'} toegevoegd aan je winkelwagen.`,
        situationChosen: (title) => `${title} gekozen. Gedeelde onderdelen worden samengevoegd.`, peopleChanged: (label) => `Aantal personen aangepast naar ${label}.`,
        readyToReview: 'Je uitrusting staat klaar om te controleren.', chooseExtra: 'Kies een extra situatie.', extraClosed: 'Extra situatie gesloten.', situationRemoved: 'Situatie verwijderd.', selectionCleared: 'Je selectie is gewist.', productChosen: (title) => `${title} gekozen.`, productRemoved: 'Product uit je pakket verwijderd.', productAdded: (title) => `${title} toegevoegd aan je pakket.`, variantChosen: (title) => `Variant gekozen: ${title}.`, product: 'Product', thisProduct: 'dit product', quickView: (title) => `${title} snel bekijken`, decreaseProduct: (title) => `Verlaag aantal van ${title}`, increaseProduct: (title) => `Verhoog aantal van ${title}`, from: 'Van', add: (price) => `TOEVOEGEN — ${price}`, selected: 'GEKOZEN', outOfStock: 'Niet op voorraad', lowStock: (count) => `Nog ${count} op voorraad`, fullProductPage: 'Volledige productpagina bekijken', keyFeatures: 'Belangrijkste eigenschappen', unavailableShort: 'Niet beschikbaar'
      },
      en: {
        people: (count, fivePlus) => fivePlus ? '5+ people' : `${count} ${count === '1' ? 'person' : 'people'}`,
        water: (litres, fivePlus) => `${fivePlus ? 'at least ' : ''}${litres} litres`, waterStart: (amount) => `For your 72-hour essentials: ${amount} of stored drinking water. A water filter is only an addition.`, waterGeneral: 'Store drinking water appropriate to your situation. The 72-hour calculation appears once you select preparedness at home for 72 hours.', fivePlusNote: '5+ is a starting quantity; check the amount per person.', suitableExtra: (category) => `Suitable addition: you have not yet selected ${category.toLowerCase()}.`, quantityOf: (title) => `Quantity of ${title}`, compareModels: 'Compare models', remove: 'Remove', decrease: 'Decrease quantity', increase: 'Increase quantity', items: (count) => `${count} item${count === 1 ? '' : 's'}`, products: (count) => `${count} product${count === 1 ? '' : 's'}`, empty: 'No products selected yet.<span>Choose what you want to add for each need below.</span>', showAll: (count) => `View all ${count} selected products`, noSituation: 'No situation selected yet', chooseSituation: 'Choose a situation to get started.', selectionAdded: 'Selection already added', addProducts: (count) => `Add ${count} product${count === 1 ? '' : 's'}`, addSelection: 'Add my selection', recommendationsFor: (situation) => `Your recommendations for ${situation}`, recommendations: 'Your recommendations', recommendationSub: (count) => `Based on your situation, we selected ${count} suitable products. Add only what you need.`, chooseFirst: 'Choose a situation first. The products that suit you will then appear here.', recommendedProducts: (count) => `${count} recommended product${count === 1 ? '' : 's'}`, editSituation: 'Edit situation', unavailable: (count) => `${count} recommended product${count === 1 ? ' is' : 's are'} currently unavailable and therefore not shown.`, currentChoice: '✓ Your current choice', selectedNow: '✓ Currently selected', chooseModel: (price) => `Choose this model — ${price}`, noAlternative: 'No alternative is currently available for this need.', compareProducts: 'Compare products', compareOther: 'Compare other models', compareIntro: 'Compare the key differences and choose what best suits your emergency kit.', unavailableProduct: 'This product is currently unavailable.', variant: 'Variant', variantOrColor: 'Variant or colour', quantity: 'Quantity', adding: 'Adding...', checkout: 'Secure checkout', alreadyAdded: 'This selection has already been added.', viewCart: 'View cart', addingSelection: 'Adding selection...', added: 'Added to your cart.', checkoutDirect: 'or proceed directly to checkout.', productsAdded: (count) => `${count} product${count === 1 ? ' has' : 's have'} been added to your cart.`, situationChosen: (title) => `${title} selected. Shared items are combined.`, peopleChanged: (label) => `Number of people changed to ${label}.`, readyToReview: 'Your equipment is ready to review.', chooseExtra: 'Choose an additional situation.', extraClosed: 'Additional situation closed.', situationRemoved: 'Situation removed.', selectionCleared: 'Your selection has been cleared.', productChosen: (title) => `${title} selected.`, productRemoved: 'Product removed from your kit.', productAdded: (title) => `${title} added to your kit.`, variantChosen: (title) => `Variant selected: ${title}.`, product: 'Product', thisProduct: 'this product', quickView: (title) => `Quick view ${title}`, decreaseProduct: (title) => `Decrease quantity of ${title}`, increaseProduct: (title) => `Increase quantity of ${title}`, from: 'Was', add: (price) => `ADD — ${price}`, selected: 'SELECTED', outOfStock: 'Out of stock', lowStock: (count) => `Only ${count} left in stock`, fullProductPage: 'View full product page', keyFeatures: 'Key features', unavailableShort: 'Unavailable'
      },
      de: {
        people: (count, fivePlus) => fivePlus ? '5+ Personen' : `${count} ${count === '1' ? 'Person' : 'Personen'}`,
        water: (litres, fivePlus) => `${fivePlus ? 'mindestens ' : ''}${litres} Liter`, waterStart: (amount) => `Für Ihre 72-Stunden-Grundausstattung: ${amount} gelagertes Trinkwasser. Ein Wasserfilter ist nur eine Ergänzung.`, waterGeneral: 'Lagern Sie passend zu Ihrer Situation Trinkwasser. Die 72-Stunden-Berechnung erscheint, sobald Sie „72 Stunden zu Hause vorbereitet“ wählen.', fivePlusNote: '5+ ist eine Ausgangsmenge; prüfen Sie die Menge pro Person.', suitableExtra: (category) => `Passende Ergänzung: Sie haben ${category.toLowerCase()} noch nicht ausgewählt.`, quantityOf: (title) => `Menge von ${title}`, compareModels: 'Modelle vergleichen', remove: 'Entfernen', decrease: 'Menge verringern', increase: 'Menge erhöhen', items: (count) => `${count} Artikel`, products: (count) => `${count} ${count === 1 ? 'Produkt' : 'Produkte'}`, empty: 'Noch keine Produkte ausgewählt.<span>Wählen Sie unten für jeden Bedarf aus, was Sie hinzufügen möchten.</span>', showAll: (count) => `Alle ${count} ausgewählten Produkte ansehen`, noSituation: 'Noch keine Situation ausgewählt', chooseSituation: 'Wählen Sie zunächst eine Situation.', selectionAdded: 'Auswahl bereits hinzugefügt', addProducts: (count) => `${count} ${count === 1 ? 'Produkt' : 'Produkte'} hinzufügen`, addSelection: 'Meine Auswahl hinzufügen', recommendationsFor: (situation) => `Ihre Empfehlungen für ${situation}`, recommendations: 'Ihre Empfehlungen', recommendationSub: (count) => `Auf Grundlage Ihrer Situation haben wir ${count} passende Produkte ausgewählt. Fügen Sie nur hinzu, was Sie benötigen.`, chooseFirst: 'Wählen Sie zuerst eine Situation. Danach erscheinen hier die passenden Produkte.', recommendedProducts: (count) => `${count} ${count === 1 ? 'empfohlenes Produkt' : 'empfohlene Produkte'}`, editSituation: 'Situation ändern', unavailable: (count) => `${count} ${count === 1 ? 'empfohlenes Produkt ist' : 'empfohlene Produkte sind'} derzeit nicht lieferbar und werden daher nicht angezeigt.`, currentChoice: '✓ Ihre aktuelle Auswahl', selectedNow: '✓ Aktuell ausgewählt', chooseModel: (price) => `Dieses Modell wählen — ${price}`, noAlternative: 'Für diesen Bedarf ist derzeit keine Alternative verfügbar.', compareProducts: 'Produkte vergleichen', compareOther: 'Andere Modelle vergleichen', compareIntro: 'Vergleichen Sie die wichtigsten Unterschiede und wählen Sie das Modell, das am besten zu Ihrem Notfallpaket passt.', unavailableProduct: 'Dieses Produkt ist derzeit nicht verfügbar.', variant: 'Variante', variantOrColor: 'Variante oder Farbe', quantity: 'Menge', adding: 'Wird hinzugefügt...', checkout: 'Sicher zur Kasse', alreadyAdded: 'Diese Auswahl wurde bereits hinzugefügt.', viewCart: 'Warenkorb ansehen', addingSelection: 'Auswahl wird hinzugefügt...', added: 'Zum Warenkorb hinzugefügt.', checkoutDirect: 'oder gehen Sie direkt zur Kasse.', productsAdded: (count) => `${count} ${count === 1 ? 'Produkt wurde' : 'Produkte wurden'} zu Ihrem Warenkorb hinzugefügt.`, situationChosen: (title) => `${title} ausgewählt. Gemeinsame Bestandteile werden zusammengeführt.`, peopleChanged: (label) => `Personenzahl auf ${label} geändert.`, readyToReview: 'Ihre Ausrüstung kann jetzt geprüft werden.', chooseExtra: 'Wählen Sie eine zusätzliche Situation.', extraClosed: 'Zusätzliche Situation geschlossen.', situationRemoved: 'Situation entfernt.', selectionCleared: 'Ihre Auswahl wurde gelöscht.', productChosen: (title) => `${title} ausgewählt.`, productRemoved: 'Produkt aus Ihrem Paket entfernt.', productAdded: (title) => `${title} zu Ihrem Paket hinzugefügt.`, variantChosen: (title) => `Variante ausgewählt: ${title}.`, product: 'Produkt', thisProduct: 'dieses Produkt', quickView: (title) => `${title} schnell ansehen`, decreaseProduct: (title) => `Menge von ${title} verringern`, increaseProduct: (title) => `Menge von ${title} erhöhen`, from: 'Statt', add: (price) => `HINZUFÜGEN — ${price}`, selected: 'AUSGEWÄHLT', outOfStock: 'Nicht auf Lager', lowStock: (count) => `Nur noch ${count} auf Lager`, fullProductPage: 'Vollständige Produktseite ansehen', keyFeatures: 'Wichtigste Eigenschaften', unavailableShort: 'Nicht verfügbar'
      }
    };
    const uiCopy = uiTranslations[discountLanguage] || uiTranslations.nl;

    const clamp = (value, min, max) => {
      const number = Number.isFinite(Number(value)) ? Number(value) : min;
      return Math.max(min, Math.min(max, number));
    };

    const activeSituations = () => state.selectedSituationIds
      .map((id) => situations.get(id))
      .filter(Boolean);

    const hasSituation = (id) => state.selectedSituationIds.includes(id);
    const hasOutdoor = () => hasSituation('buiten_zonder_stroom');
    const has72HourBasis = () => hasSituation('thuis_72_uur');

    const durationOption = () => durationOptions.find((item) => item.id === state.duration) || durationOptions[0] || { id: state.duration, title: state.duration };
    const peopleNumber = () => (state.people === '5' ? 5 : Number(state.people || 1));
    const isFivePlus = () => state.people === '5';
    const peopleLabel = () => uiCopy.people(state.people, isFivePlus());

    const waterLiters = () => peopleNumber() * Number(config.water_rule?.liters_per_person_per_day || 3) * Number(config.water_rule?.days || 3);
    const waterCopy = () => uiCopy.water(waterLiters(), isFivePlus());

    const durationRule = () => {
      if (!hasOutdoor()) return null;
      return config.duration_rules?.buiten_zonder_stroom?.[state.duration] || null;
    };

    const choicesForCategory = (categoryId) => productChoices.get(categoryId) || [];

    const baseChoiceFor = (categoryId) => {
      const choices = choicesForCategory(categoryId);
      if (!choices.length) return null;
      const preferredId = state.choiceByCategory[categoryId];
      return choices.find((choice) => choice.choice_id === preferredId) || choices[0];
    };

    const variantStateKey = (categoryId, choiceId) => `${categoryId}:${choiceId}`;

    const variantForChoice = (categoryId, choice) => {
      if (!choice) return null;
      const variants = Array.isArray(choice.variants) ? choice.variants.filter((variant) => variant.available && variant.id) : [];
      if (!variants.length) {
        return {
          id: choice.variant_id,
          title: choice.variant_title,
          available: choice.available,
          price_cents: choice.price_cents,
          compare_at_price_cents: choice.compare_at_price_cents,
          image: choice.image,
          image_srcset: choice.image_srcset,
          image_alt: choice.image_alt,
          image_width: choice.image_width,
          image_height: choice.image_height,
          inventory: choice.inventory,
          inventory_tracked: choice.inventory_tracked,
        };
      }

      const savedVariantId = Number(state.variantByChoice[variantStateKey(categoryId, choice.choice_id)]);
      return variants.find((variant) => variant.id === savedVariantId)
        || variants.find((variant) => variant.id === Number(choice.variant_id))
        || variants[0];
    };

    const choiceWithVariant = (categoryId, choice) => {
      if (!choice) return null;
      const variant = variantForChoice(categoryId, choice);
      if (!variant?.id) return null;
      return {
        ...choice,
        selected_variant: variant,
        variant_id: Number(variant.id),
        variant_title: variant.title,
        price_cents: Number(variant.price_cents || choice.price_cents || 0),
        compare_at_price_cents: Number(variant.compare_at_price_cents || choice.compare_at_price_cents || 0),
        image: variant.image || choice.image || '',
        image_srcset: variant.image_srcset || choice.image_srcset || '',
        image_alt: variant.image_alt || choice.image_alt || choice.display_title || choice.title || '',
        image_width: Number(variant.image_width || choice.image_width || 0),
        image_height: Number(variant.image_height || choice.image_height || 0),
        inventory: variant.inventory,
        inventory_tracked: Boolean(variant.inventory_tracked),
        available: Boolean(variant.available),
      };
    };

    const selectedChoiceFor = (categoryId) => choiceWithVariant(categoryId, baseChoiceFor(categoryId));

    const hasSellableChoice = (categoryId) => Boolean(selectedChoiceFor(categoryId));

    const categoryForNeed = (need) => (need?.category_id ? categories.get(need.category_id) : null);

    const highestPriority = (current, next) => {
      if (!next) return current;
      return (PRIORITY_RANK[next] || 0) > (PRIORITY_RANK[current] || 0) ? next : current;
    };

    const priorityForLink = (link, requirement) => {
      const rule = durationRule();
      const overrides = rule?.priority_overrides || {};
      const override = overrides[link.id] || overrides[requirement?.category_id];
      return highestPriority(link.priority, override);
    };

    const scrollToShell = () => {
      root.querySelector('.asb__shell')?.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    };

    const persist = () => {
      if (isDesignMode()) return;
      setStoredState(storageKey, {
        view: state.view,
        selectedSituationIds: state.selectedSituationIds,
        people: state.people,
        duration: state.duration,
        categoryStatus: state.categoryStatus,
        choiceByCategory: state.choiceByCategory,
        variantByChoice: state.variantByChoice,
        qtyByCategory: state.qtyByCategory,
        removedCategories: state.removedCategories,
      });
    };

    const validSituationIds = (ids) => {
      if (!Array.isArray(ids)) return [];
      return ids.filter((id, index, all) => situations.has(id) && all.indexOf(id) === index);
    };

    const setView = (viewName, options = {}) => {
      // situatie en profielvragen staan sinds de samenvoeging in dezelfde stap-1-view
      const view = viewName === 'questions' ? 'situations' : viewName;
      state.view = view;
      Object.entries(views).forEach(([name, panel]) => {
        if (!panel) return;
        panel.hidden = name !== view;
      });
      renderStepBar();
      if (summaryAside) summaryAside.hidden = view === 'cart';
      if (shellNode) shellNode.classList.toggle('asb__shell--single', view === 'cart');
      if (options.scroll) scrollToShell();
      if (options.persist !== false) persist();
    };

    const stepStateFor = (view) => {
      if (view === 'cart') return { situation: 'done', equipment: 'done', review: 'active' };
      if (view === 'result') return { situation: 'done', equipment: 'active', review: 'next' };
      return { situation: 'active', equipment: 'next', review: 'next' };
    };

    const renderStepBar = () => {
      if (!stepBar) return;
      const states = stepStateFor(state.view);
      stepBar.hidden = !state.selectedSituationIds.length;
      stepBar.querySelectorAll('[data-step]').forEach((item) => {
        const status = states[item.dataset.step] || 'next';
        item.classList.toggle('is-done', status === 'done');
        item.classList.toggle('is-active', status === 'active');
        item.classList.toggle('is-next', status === 'next');
        const button = item.querySelector('button');
        if (button) {
          button.setAttribute('aria-current', status === 'active' ? 'step' : 'false');
          button.disabled = status === 'next' && !state.selectedSituationIds.length;
        }
      });
    };

    // Alle behoeften die bij de gekozen situatie(s) horen. Geen adviesniveaus:
    // wat relevant is, is relevant.
    const mergedNeeds = () => {
      const merged = new Map();

      activeSituations().forEach((situation) => {
        (situation.requirements || []).forEach((link) => {
          const requirement = requirements.get(link.id);
          if (!requirement) return;
          const priority = priorityForLink(link, requirement);
          const existing = merged.get(link.id);
          if (!existing) {
            merged.set(link.id, {
              ...requirement,
              priority,
              situations: [situation.title],
            });
            return;
          }
          if (!existing.situations.includes(situation.title)) existing.situations.push(situation.title);
          existing.priority = highestPriority(existing.priority, priority);
        });
      });

      return [...merged.values()].sort((a, b) => {
        const categoryA = categoryForNeed(a);
        const categoryB = categoryForNeed(b);
        const themeA = themes.get(categoryA?.theme_id);
        const themeB = themes.get(categoryB?.theme_id);
        const themeRank = Number(themeA?.sort_order || 999) - Number(themeB?.sort_order || 999);
        if (themeRank) return themeRank;
        const categoryRank = Number(categoryA?.sort_order || 999) - Number(categoryB?.sort_order || 999);
        if (categoryRank) return categoryRank;
        const priorityRank = (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0);
        return priorityRank || Number(a.sort_order || 0) - Number(b.sort_order || 0);
      });
    };

    // Eén status in stap 2: een product staat in de selectie of niet.
    const categoryStatusForNeed = (need) => {
      if (!need?.category_id) return STATUS.OPEN;
      if (state.categoryStatus[need.category_id] !== STATUS.SELECTED) return STATUS.OPEN;
      return hasSellableChoice(need.category_id) ? STATUS.SELECTED : STATUS.OPEN;
    };

    // Zelf-regelen-items zijn informatief: niets om af te vinken.
    const statusForNeed = (need) => (need.fulfilment_type === 'self' ? STATUS.SELF_OPEN : categoryStatusForNeed(need));

    /* Eén centrale bron voor alle voortgangstellingen. Onderdelen waarop de
       bezoeker niets kan beslissen (advies zonder product, leeslijst) zijn
       informatief en tellen nergens mee. Korting staat hier los van. */
    const progressTypeFor = (need) => {
      if (need && need.progress_type) return need.progress_type;
      if (!need || !need.category_id) return 'informational';
      if (need.fulfilment_type === 'self') return 'informational';
      if (!hasSellableChoice(need.category_id)) return 'informational';
      return 'actionable_product';
    };
    const countsInProgress = (need) => progressTypeFor(need) !== 'informational';
    const progressDone = (need, needs) => [STATUS.SELECTED, STATUS.SELF_DONE].includes(statusForNeed(need, needs));
    const progressFor = (list, needs) => {
      const items = (list || []).filter(countsInProgress);
      const done = items.filter((need) => progressDone(need, needs)).length;
      return { total: items.length, done, complete: items.length > 0 && done === items.length };
    };

    // Toevoegen of verwijderen; verder geen statussen meer in stap 2.
    const setCategorySelected = (categoryId, selected) => {
      if (!categoryId) return;
      if (selected) {
        if (!hasSellableChoice(categoryId)) return;
        state.categoryStatus[categoryId] = STATUS.SELECTED;
        delete state.removedCategories[categoryId];
      } else {
        delete state.categoryStatus[categoryId];
        state.removedCategories[categoryId] = true;
      }
      state.lastAddedSignature = '';
      persist();
      render();
    };

    const setQuantity = (categoryId, nextQty) => {
      const category = categories.get(categoryId);
      if (!category || category.quantity_rule === 'none') return;
      const qty = clamp(nextQty, 1, Number(category.max_qty || 99));
      state.qtyByCategory[categoryId] = qty;
      state.lastAddedSignature = '';
      persist();
      render();
    };

    const quantityForCategory = (categoryId) => {
      const category = categories.get(categoryId);
      if (!category || category.quantity_rule === 'none') return 0;
      const max = Number(category.max_qty || 99);
      const saved = Number(state.qtyByCategory[categoryId]);
      if (Number.isFinite(saved) && saved > 0) return clamp(saved, 1, max);

      let qty = 1;
      if (category.quantity_rule === 'per_person') qty = peopleNumber();
      if (category.quantity_rule === 'per_two_people') qty = Math.ceil(peopleNumber() / 2);
      if (category.quantity_rule === 'group_extra') qty = peopleNumber() >= 3 ? 2 : 1;

      const minimum = Number(durationRule()?.quantity_minimums?.[categoryId] || 0);
      if (minimum > 0) qty = Math.max(qty, minimum);

      return clamp(qty, 1, max);
    };

    const quantityNote = (categoryId) => {
      const notes = [];
      const category = categories.get(categoryId);
      if (!category || category.quantity_rule === 'none') return '';
      if (isFivePlus() && ['per_person', 'per_two_people', 'group_extra'].includes(category.quantity_rule)) {
        notes.push(uiCopy.fivePlusNote);
      }
      if (durationRule()?.quantity_minimums?.[categoryId]) {
        notes.push('Duur verhoogt dit startaantal.');
      }
      if (categoryId === 'water_storage_transport') notes.push('Vullen blijft zelf regelen.');
      if (categoryId === 'phone_power') notes.push('Kabels apart controleren.');
      return notes.join(' ');
    };

    const describeNeed = (need) => {
      if (need.id === 'stored_drinking_water') {
        if (has72HourBasis()) {
          return uiCopy.waterStart(waterCopy());
        }
        return uiCopy.waterGeneral;
      }
      return need.description;
    };

    const selectedLines = (needs = mergedNeeds()) => {
      const lines = new Map();
      needs.forEach((need) => {
        if (!need.category_id || statusForNeed(need, needs) !== STATUS.SELECTED) return;
        const product = selectedChoiceFor(need.category_id);
        const category = categories.get(need.category_id);
        const theme = themes.get(category?.theme_id);
        if (!product || !category) return;

        const key = `${need.category_id}:${product.variant_id}`;
        const quantity = quantityForCategory(need.category_id);
        if (quantity <= 0) return;
        const existing = lines.get(key);
        if (existing) {
          existing.quantity = Math.max(existing.quantity, quantity);
          existing.categoryIds.push(need.category_id);
          existing.needTitles.push(need.title);
          existing.needIds.push(need.id);
          existing.categoryTitles.push(category.title);
          return;
        }

        lines.set(key, {
          product,
          quantity,
          categoryIds: [need.category_id],
          needTitles: [need.title],
          needIds: [need.id],
          categoryTitles: [category.title],
          themeTitle: theme?.title || '',
          discountEligible: productDiscountEligible(product),
        });
      });
      return [...lines.values()];
    };

    const selectionSignature = (lines = selectedLines()) => lines
      .map((line) => `${line.categoryIds[0]}:${line.product.variant_id}:${line.quantity}`)
      .sort()
      .join('|');

    const countState = (needs = mergedNeeds(), selectedProductLines = null) => {
      const counts = {
        total: needs.length,
        selfTotal: 0,
        selfOpen: 0,
        selfDone: 0,
        selected: 0,
        owned: 0,
        skipped: 0,
        open: 0,
        openProducts: 0,
        productLines: 0,
        productUnits: 0,
        subtotalCents: 0,
        eligibleProductCount: 0,
        eligibleCount: 0,
      };

      needs.forEach((need) => {
        const status = statusForNeed(need, needs);
        if (need.fulfilment_type === 'self') counts.selfTotal += 1;
        if (status === STATUS.SELF_OPEN) counts.selfOpen += 1;
        if (status === STATUS.SELF_DONE) counts.selfDone += 1;
        if (status === STATUS.SELECTED) counts.selected += 1;
        if ([STATUS.OPEN, STATUS.SELF_OPEN].includes(status) && countsInProgress(need)) counts.open += 1;
        if (status === STATUS.OPEN && countsInProgress(need)) counts.openProducts += 1;
      });

      const progress = progressFor(needs, needs);
      counts.progressTotal = progress.total;
      counts.progressDone = progress.done;

      const lines = Array.isArray(selectedProductLines) ? selectedProductLines : selectedLines(needs);
      counts.productLines = lines.length;
      counts.productUnits = lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
      counts.subtotalCents = lines.reduce((sum, line) => sum + (Number(line.product.price_cents || 0) * Number(line.quantity || 0)), 0);
      const eligibleLines = lines.filter((line) => line.discountEligible);
      const allKeys = new Set();
      const eligibleKeys = new Set();
      lines.forEach((line) => {
        const key = line.product.product_id || line.product.handle || line.product.choice_id || String(line.product.variant_id || '');
        allKeys.add(key);
      });
      eligibleLines.forEach((line) => {
        const key = line.product.product_id || line.product.handle || line.product.choice_id || String(line.product.variant_id || '');
        eligibleKeys.add(key);
      });
      counts.uniqueProducts = allKeys.size;
      counts.eligibleProductCount = eligibleKeys.size;
      counts.eligibleCount = eligibleLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
      return counts;
    };

    const discountPercentFor = (eligibleCount) => {
      if (!discountPolicy.active) return 0;
      const percentage = bundleDiscountTiers.reduce((current, tier) => (
        eligibleCount >= tier.min ? tier.percentage : current
      ), 0);
      return Math.min(percentage, maxBundleDiscountPercentage);
    };

    const discountTiers = () => bundleDiscountTiers.map((tier) => ({ ...tier }));

    const maxDiscountPercentage = () => {
      const maxTierPercentage = discountTiers().reduce((max, tier) => Math.max(max, Number(tier.percentage || 0)), 0);
      return Math.min(maxTierPercentage, maxBundleDiscountPercentage);
    };

    // Eén bron voor de balk, de rechterkolom, stap 3 en de drawer.
    const discountState = (counts, totals = discountTotals(counts)) => {
      const tiers = discountTiers();
      const next = tiers.find((tier) => counts.eligibleCount < tier.min) || null;
      const maxMin = tiers.length ? tiers[tiers.length - 1].min : 0;
      const first = tiers.length ? tiers[0] : null;
      return {
        active: Boolean(discountPolicy.active && tiers.length),
        tiers,
        qualifyingUnits: counts.eligibleCount,
        units: counts.productUnits,
        percentage: totals.percentage,
        totals,
        next,
        missing: next ? Math.max(1, next.min - counts.eligibleCount) : 0,
        maxReached: Boolean(!next && totals.percentage > 0),
        firstTier: first,
        toFirst: first ? Math.max(0, first.min - counts.eligibleCount) : 0,
        progress: maxMin ? Math.max(0, Math.min(100, (counts.eligibleCount / maxMin) * 100)) : 0,
      };
    };

    const selectionSnapshot = (needs = mergedNeeds()) => {
      const lines = selectedLines(needs);
      const counts = countState(needs, lines);
      const totals = discountTotals(counts);
      const discount = discountState(counts, totals);
      return {
        needs,
        lines,
        counts,
        totals,
        discount,
        signature: selectionSignature(lines),
      };
    };

    const nextThresholdLine = (info) => {
      if (!info.active || !info.next) return '';
      return discountCopy.next(info.missing, formatPercent(info.next.percentage));
    };

    const discountTotals = (counts) => {
      const percentage = discountPercentFor(counts.eligibleCount);
      return {
        percentage,
        discountCents: 0,
        finalCents: Number(counts.subtotalCents || 0),
      };
    };

    const shippingState = (counts) => {
      const qualifyingCents = Number(counts.subtotalCents || 0);
      const visible = Boolean(freeShippingThresholdCents > 0 && counts.productLines);
      const remainingCents = Math.max(0, freeShippingThresholdCents - qualifyingCents);
      const threshold = formatMoney(freeShippingThresholdCents);
      return {
        visible,
        reached: false,
        progress: freeShippingThresholdCents > 0 ? clamp((qualifyingCents / freeShippingThresholdCents) * 100, 0, 100) : 0,
        copy: remainingCents === 0
          ? discountCopy.shippingThreshold(threshold)
          : discountCopy.shippingBelow(formatMoney(remainingCents), threshold),
      };
    };

    const renderCommerceMeta = (counts) => {
      const shipping = shippingState(counts);
      shippingNodes.forEach((node) => {
        node.hidden = !shipping.visible;
        node.classList.toggle('is-reached', shipping.reached);
      });
      shippingStatusNodes.forEach((node) => {
        node.textContent = shipping.visible ? shipping.copy : '';
      });
      shippingMeterNodes.forEach((node) => {
        node.style.width = `${shipping.progress}%`;
      });
      taxNoteNodes.forEach((node) => {
        const showTax = Boolean(counts.productLines && sales.taxes_included);
        node.hidden = !showTax;
        node.textContent = showTax ? 'incl. btw' : '';
      });
    };

    const bundleCopy = (counts, options = {}) => {
      const info = discountState(counts);
      if (!info.tiers.length) return null;
      if (!info.active && !options.showInactive) return null;
      const maxPct = formatPercent(maxDiscountPercentage());

      let head;
      const selected = discountCopy.selected(counts.eligibleCount);
      const context = `${discountCopy.cartNote} ${discountCopy.checkoutNote}`;
      if (!info.percentage) {
        head = discountCopy.overview(maxPct);
      } else {
        head = discountCopy.expected(formatPercent(info.percentage));
      }
      const next = !info.percentage && info.next ? nextThresholdLine(info) : '';
      const sub = [selected, next, context].filter(Boolean).join(' ');

      return { head, sub, info };
    };

    const discountBarHtml = (counts) => {
      const parts = bundleCopy(counts, { showInactive: true });
      if (!parts) return '';
      return `
        <p class="asb__bundle-head">${escapeHTML(parts.head)}</p>
        <p class="asb__bundle-sub">${escapeHTML(parts.sub)}</p>
        <span class="asb__bundle-rail" aria-hidden="true"><i style="width: ${parts.info.progress}%"></i></span>
      `;
    };

    // Stap 3: dezelfde boodschap, zonder voortgangsbalk.
    const bundleBannerHtml = (counts) => {
      const parts = bundleCopy(counts);
      if (!parts) return '';
      return `
        <p class="asb__bundle-head">${escapeHTML(parts.head)}</p>
        <p class="asb__bundle-sub">${escapeHTML(parts.sub)}</p>
      `;
    };

    const renderTierModal = (counts, info = discountState(counts)) => {
      if (!tierRows) return;
      if (!info.tiers.length) {
        tierRows.innerHTML = '';
        if (tierModal) tierModal.hidden = true;
        state.tiersOpen = false;
        return;
      }
      tierRows.innerHTML = info.tiers.map((tier, index) => {
        const upper = info.tiers[index + 1] ? info.tiers[index + 1].min - 1 : 0;
        const unitCopy = upper
          ? (upper > tier.min
            ? `${tier.min}\u2013${upper} ${discountCopy.tierUnits(upper).replace(/^\d+\s*/, '')}`
            : discountCopy.tierUnits(tier.min))
          : `${discountCopy.tierUnits(tier.min)}+`;
        return `<li${info.active && counts.eligibleCount >= tier.min ? ' class="is-done"' : ''}><span>${escapeHTML(unitCopy)}</span><b>${formatPercent(tier.percentage)}%</b></li>`;
      }).join('');
      if (tierTitle) tierTitle.textContent = discountCopy.tierTitle;
      if (tierExplanation) tierExplanation.textContent = discountCopy.tierExplanation;
      if (tierModal && !state.tiersOpen) tierModal.hidden = true;
    };

    const renderHeroBundle = (info) => {
      if (!heroBundle) return;
      const maxPercentage = maxDiscountPercentage();
      const hasTiers = Boolean(info.tiers.length && maxPercentage > 0);
      heroBundle.hidden = !hasTiers;
      if (!hasTiers) return;
      const maxNode = heroBundle.querySelector('[data-hero-bundle-max]');
      if (maxNode) maxNode.textContent = `TOT ${formatPercent(maxPercentage)}%`;
      if (heroBundleTitle) {
        const maximum = `${formatPercent(maxPercentage)}%`;
        const title = discountCopy.hero(formatPercent(maxPercentage));
        heroBundleTitle.innerHTML = escapeHTML(title).replace(
          escapeHTML(maximum),
          `<em data-hero-bundle-max>${escapeHTML(maximum)}</em>`,
        );
      }
      if (heroBundleText) heroBundleText.textContent = discountCopy.heroText;
    };

    // Maximaal één aanvulling: openstaande categorie, op voorraad, telt mee
    // voor het bundelvoordeel. Nadrukkelijker zodra nog één product ontbreekt.
    const nextSalesSuggestion = (needs, counts, info = discountState(counts), lines = selectedLines(needs)) => {
      if (!counts.productLines) return '';
      const chosen = new Set(lines.flatMap((line) => line.categoryIds));
      const candidate = needs.find((need) => {
        if (need.fulfilment_type === 'self' || !need.category_id) return false;
        if (chosen.has(need.category_id)) return false;
        if (state.removedCategories[need.category_id]) return false;
        const category = categories.get(need.category_id);
        if (!category) return false;
        const choice = selectedChoiceFor(need.category_id);
        return Boolean(choice && choice.available !== false && productDiscountEligible(choice));
      });
      if (!candidate) return '';
      const category = categories.get(candidate.category_id);
      if (info.active && info.next && info.missing === 1) {
        return discountCopy.next(1, formatPercent(info.next.percentage));
      }
      return uiCopy.suitableExtra(category.title);
    };

    const setCartMessage = (message, type = '') => {
      if (!cartStatus) return;
      cartStatus.classList.toggle('is-error', type === 'error');
      cartStatus.classList.toggle('is-success', type === 'success');
      cartStatus.innerHTML = message;
    };

    const centsToMajor = (cents) => Number((Number(cents || 0) / 100).toFixed(2));

    const analyticsItems = (lines) => lines.map((line, index) => ({
      item_id: String(line.product.variant_id),
      item_name: line.product.display_title || line.product.title,
      item_variant: line.product.variant_title || undefined,
      item_category: unique(line.categoryTitles).join(', '),
      item_category2: line.themeTitle || undefined,
      price: centsToMajor(line.product.price_cents),
      quantity: Number(line.quantity || 1),
      index,
    }));

    const situationDetail = () => ({
      source: 'alfa_situation_builder',
      situation_ids: state.selectedSituationIds.slice(),
      situations: activeSituations().map((item) => item.title),
      people: state.people,
      duration: hasOutdoor() ? state.duration : null,
    });

    const pushDataLayer = (payload) => {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
      } catch (error) {
        // Analytics must never block the builder.
      }
    };

    const trackFunnelEvent = (name, detail = {}) => {
      const payload = { ...situationDetail(), ...detail };
      document.dispatchEvent(new CustomEvent(`alfa:situation-builder:${name}`, { detail: payload }));
      pushDataLayer({
        event: `situation_builder_${name}`,
        situation_builder: payload,
      });
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', `SituationBuilder_${name}`, payload);
      }
    };

    const trackAddToCart = (lines, counts, bundleKey) => {
      const totals = discountTotals(counts);
      const items = analyticsItems(lines);
      // Analytics contract: value is the subtotal of only the products added by
      // this builder action. No applied Shopify discount is known at this point,
      // so discount remains 0 and the non-guaranteed tier is reported separately.
      // Keep eligible_categories compatible with its historic meaning: the
      // number of unique qualifying Shopify products (despite the legacy name).
      const payload = {
        ...situationDetail(),
        bundle_key: bundleKey,
        value: centsToMajor(counts.subtotalCents),
        subtotal: centsToMajor(counts.subtotalCents),
        discount: 0,
        expected_discount_percentage: totals.percentage,
        currency: sales.currency || 'EUR',
        eligible_categories: counts.eligibleProductCount,
        eligible_units: counts.eligibleCount,
        items,
      };

      document.dispatchEvent(new CustomEvent('alfa:situation-builder:add_to_cart', { detail: payload }));
      pushDataLayer({
        event: 'add_to_cart',
        ecommerce: {
          currency: payload.currency,
          value: payload.value,
          items,
        },
        situation_builder: payload,
      });
      pushDataLayer({
        event: 'situation_builder_add_to_cart',
        situation_builder: payload,
      });
    };

    const makeBundleKey = (signature) => {
      const cleanSection = toKebab(sectionId).slice(0, 24) || 'builder';
      let hash = 0;
      String(signature || '').split('').forEach((char) => {
        hash = ((hash << 5) - hash) + char.charCodeAt(0);
        hash |= 0;
      });
      return `asb-${cleanSection}-${Math.abs(hash).toString(36)}-${Date.now().toString(36)}`;
    };

    const getThemeCartDrawer = () => document.getElementById('CartDrawer') || document.querySelector('cart-drawer');

    const isThemeCartDrawerOpen = (drawerNode) => {
      if (!drawerNode || drawerNode.hasAttribute('hidden')) return false;
      if (drawerNode.getAttribute('aria-hidden') === 'true') return false;
      return drawerNode.hasAttribute('open') || drawerNode.classList.contains('active') || drawerNode.classList.contains('is-open');
    };

    const openThemeCartDrawer = () => {
      const drawerNode = getThemeCartDrawer();
      if (!drawerNode) return false;

      ['show', 'open', 'showModal', 'toggle', 'expand'].some((method) => {
        if (typeof drawerNode[method] !== 'function') return false;
        try {
          drawerNode[method]();
        } catch (error) {
          return false;
        }
        return isThemeCartDrawerOpen(drawerNode);
      });

      if (isThemeCartDrawerOpen(drawerNode)) return true;
      const trigger = document.querySelector('[aria-controls="CartDrawer"], [data-cart-drawer-trigger], a[href="/cart"]');
      if (trigger && typeof trigger.click === 'function') trigger.click();
      if (isThemeCartDrawerOpen(drawerNode)) return true;

      drawerNode.removeAttribute('hidden');
      drawerNode.setAttribute('aria-hidden', 'false');
      drawerNode.setAttribute('open', '');
      drawerNode.classList.add('is-open', 'active');
      document.documentElement.classList.add('overflow-hidden');
      return true;
    };

    const themeCartDrawerSectionId = () => {
      const drawerNode = getThemeCartDrawer();
      if (!drawerNode) return '';
      return drawerNode.getAttribute('data-section-id') || drawerNode.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '') || '';
    };

    const applyThemeCartDrawerSection = (sectionId, html) => {
      if (!sectionId || !html) return false;
      const current = document.getElementById(`shopify-section-${sectionId}`);
      if (!current) return false;
      const tmp = document.createElement('div');
      tmp.innerHTML = String(html).trim();
      const fresh = tmp.querySelector(`#shopify-section-${sectionId}`) || tmp.firstElementChild;
      if (!fresh) return false;
      if (fresh.id === `shopify-section-${sectionId}`) {
        current.replaceWith(fresh);
      } else {
        current.innerHTML = tmp.innerHTML;
      }
      return true;
    };

    const refreshThemeCartDrawer = () => {
      const drawerSection = themeCartDrawerSectionId();
      if (!drawerSection) {
        openThemeCartDrawer();
        return;
      }
      fetch(`${window.location.pathname}?sections=${encodeURIComponent(drawerSection)}`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => response.json())
        .then((payload) => applyThemeCartDrawerSection(drawerSection, payload?.[drawerSection]))
        .catch(() => {})
        .then(openThemeCartDrawer);
    };

    const updateCartCount = () => {
      fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          document.querySelectorAll('[data-cart-count], cart-count, .cart-count').forEach((node) => {
            node.textContent = cart.item_count;
            node.removeAttribute('hidden');
            node.classList.toggle('is-empty', Number(cart.item_count || 0) <= 0);
          });
          document.dispatchEvent(new CustomEvent('alfa:cart:updated', { detail: { cart } }));
        })
        .catch(() => {});
    };

    const imageHtml = (product, className = 'asb__thumb') => {
      if (!product?.image) {
        return `<span class="${className} ${className}--empty" aria-hidden="true">ALFA</span>`;
      }
      return `<span class="${className}" aria-hidden="true"><img src="${escapeHTML(product.image)}" alt="" loading="lazy"></span>`;
    };

    const ratingHtml = (choice) => {
      const rating = Number(choice?.rating || 0);
      const count = Number(choice?.rating_count || 0);
      if (!rating || !count) return '';
      const width = clamp((rating / 5) * 100, 0, 100);
      return `
        <span class="asb__rating" aria-label="${escapeHTML(`${rating.toFixed(1).replace('.', ',')} van 5 op basis van ${count} reviews`)}">
          <span class="asb__stars" aria-hidden="true"><i style="width:${width}%"></i></span>
          <span>(${count})</span>
        </span>
      `;
    };

    const inventoryHtml = (choice) => {
      if (!choice?.inventory_tracked) return '';
      const inventory = Number(choice.inventory);
      if (!Number.isFinite(inventory)) return '';
      if (inventory <= 0) return `<span class="asb__inventory asb__inventory--out">${escapeHTML(uiCopy.outOfStock)}</span>`;
      if (inventory <= 10) return `<span class="asb__inventory asb__inventory--low">${escapeHTML(uiCopy.lowStock(inventory))}</span>`;
      return '';
    };

    const priceLineHtml = (choice, className = 'asb__rcard-price') => {
      const price = Number(choice?.price_cents || 0);
      const compareAt = Number(choice?.compare_at_price_cents || 0);
      const hasCompareAt = compareAt > price;
      return `
        <div class="${escapeHTML(className)}${hasCompareAt ? ' is-sale' : ''}">
          <strong class="asb__price">${formatMoney(price)}</strong>
          ${hasCompareAt ? `<s class="asb__price-compare">${formatMoney(compareAt)}</s>` : ''}
        </div>
      `;
    };

    const productImageHtml = (choice, title, sizes) => {
      if (!choice?.image) {
        return '<span>ALFA</span>';
      }

      const ratio = Number(choice.image_width || 0) / Number(choice.image_height || 0);
      const shape = ratio > 1.32 ? 'asb__product-image--wide' : ratio > 0 && ratio < 0.78 ? 'asb__product-image--tall' : 'asb__product-image--balanced';
      const srcset = choice.image_srcset ? ` srcset="${escapeHTML(choice.image_srcset)}"` : '';
      const width = Number(choice.image_width || 0) > 0 ? ` width="${Number(choice.image_width)}"` : '';
      const height = Number(choice.image_height || 0) > 0 ? ` height="${Number(choice.image_height)}"` : '';
      return `
        <img class="asb__product-img ${shape}" src="${escapeHTML(choice.image)}"${srcset} sizes="${escapeHTML(sizes)}"${width}${height} alt="${escapeHTML(choice.image_alt || title)}" loading="lazy" decoding="async">
      `;
    };

    const cardImageHtml = (categoryId, choice, title) => `
      <button type="button" class="asb__rcard-media" data-quickview-open="${escapeHTML(categoryId)}" aria-label="${escapeHTML(uiCopy.quickView(title))}">
        ${productImageHtml(choice, title, '(min-width: 1100px) 340px, (min-width: 761px) calc((100vw - 72px) / 2), calc(100vw - 32px)')}
      </button>
    `;

    const checkIconHtml = () => `
      <svg class="asb__rcard-check" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path d="M13.4 4.1 6.6 11 2.8 7.2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    const quantityControlHtml = (categoryId, choice) => {
      const category = categories.get(categoryId);
      const quantity = quantityForCategory(categoryId) || 1;
      const maxQty = Math.max(1, Number(category?.max_qty || quantity || 1));
      const title = choice?.display_title || choice?.title || uiCopy.thisProduct;
      return `
        <div class="asb__rcard-qty" role="group" aria-label="${escapeHTML(uiCopy.quantityOf(title))}">
          <button type="button" data-qty-action="decrease" data-category-id="${escapeHTML(categoryId)}" aria-label="${escapeHTML(uiCopy.decreaseProduct(title))}" ${quantity <= 1 ? 'disabled' : ''}>-</button>
          <span aria-live="polite">${quantity}</span>
          <button type="button" data-qty-action="increase" data-category-id="${escapeHTML(categoryId)}" aria-label="${escapeHTML(uiCopy.increaseProduct(title))}" ${quantity >= maxQty ? 'disabled' : ''}>+</button>
        </div>
      `;
    };

    const cardPriceNoteHtml = (choice) => {
      const price = Number(choice?.price_cents || 0);
      const compareAt = Number(choice?.compare_at_price_cents || 0);
      if (!(price > 0 && compareAt > price)) return '';
      return `<div class="asb__rcard-price asb__rcard-price--compare"><span>${escapeHTML(uiCopy.from)}</span><s class="asb__price-compare">${formatMoney(compareAt)}</s></div>`;
    };

    const cardAddLabel = (choice) => uiCopy.add(formatMoney(choice?.price_cents || 0));

    const cardActionHtml = (categoryId, choice, selected) => {
      if (!selected) {
        return `<button type="button" class="asb__button asb__button--primary asb__rcard-add" data-add-category="${escapeHTML(categoryId)}">${escapeHTML(cardAddLabel(choice))}</button>`;
      }

      return `
        <div class="asb__rcard-selected-controls">
          ${quantityControlHtml(categoryId, choice)}
          <span class="asb__rcard-selected-state">
            ${checkIconHtml()}
            <span>${escapeHTML(uiCopy.selected)}</span>
          </span>
        </div>
      `;
    };

    const cardFooterHtml = (categoryId, title, selected, hasAlternatives) => {
      const detailButton = `<button type="button" class="asb__text-action asb__rcard-detail" data-quickview-open="${escapeHTML(categoryId)}" aria-label="${escapeHTML(`${title} snel bekijken`)}">Details</button>`;
      const modelLabel = uiCopy.compareModels;
      return `
        <div class="asb__rcard-foot">
          <span class="asb__rcard-foot-actions">
            ${detailButton}
            ${hasAlternatives ? `<button type="button" class="asb__text-action asb__rcard-model" data-model-open="${escapeHTML(categoryId)}">${modelLabel}</button>` : ''}
          </span>
          <span class="asb__rcard-foot-side">
            ${selected ? `<button type="button" class="asb__text-action asb__rcard-remove" data-remove-category="${escapeHTML(categoryId)}">${escapeHTML(uiCopy.remove)}</button>` : ''}
          </span>
        </div>
      `;
    };

    const variantPickerHtml = (categoryId, choice) => {
      const variants = Array.isArray(choice?.variants) ? choice.variants.filter((variant) => variant.id) : [];
      if (variants.length <= 1) return '';
      const options = variants.map((variant) => {
        const selected = Number(variant.id) === Number(choice.variant_id);
        const price = variant.price_cents !== choice.price_cents ? ` · ${formatMoney(variant.price_cents)}` : '';
        const label = `${variant.title || 'Standaard'}${price}${variant.available ? '' : ' · niet beschikbaar'}`;
        return `<option value="${variant.id}" ${selected ? 'selected' : ''} ${variant.available ? '' : 'disabled'}>${escapeHTML(label)}</option>`;
      }).join('');

      return `
        <label class="asb__variant-picker asb__variant-picker--compact">
          <span class="asb__visually-hidden">Variant</span>
          <select data-variant-select data-category-id="${escapeHTML(categoryId)}" data-choice-id="${escapeHTML(choice.choice_id)}">
            ${options}
          </select>
        </label>
      `;
    };

    const renderSituationCards = () => {
      root.querySelectorAll('[data-situation-card]').forEach((button) => {
        const selected = hasSituation(button.dataset.situationId);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    };

    const renderQuestionControls = () => {
      root.querySelectorAll('[data-person]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.person === state.people));
      });
      root.querySelectorAll('[data-duration]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.duration === state.duration));
      });

      if (durationField) durationField.hidden = !hasOutdoor();
      const durationCopy = durationRule()?.copy;
      if (durationNote) {
        durationNote.textContent = hasOutdoor()
          ? (durationCopy || 'Bij langere duur verschuift het advies naar extra energie, waterfiltering en warmte.')
          : 'Deze vraag verschijnt alleen bij buiten zonder stroom.';
      }
      if (questionContext) {
        const names = activeSituations().map((item) => item.title).join(', ');
        questionContext.textContent = names ? `Gekozen: ${names}.` : 'Beantwoord alleen de vragen die het advies veranderen.';
      }
      if (waterHint) {
        waterHint.textContent = has72HourBasis()
          ? `Voor 72 uur is dit ${waterCopy()} drinkwater voor ${peopleLabel()}.`
          : 'De 72-uursberekening verschijnt wanneer je 72 uur thuis voorbereid kiest.';
      }
    };

    const renderAddGrid = () => {
      if (!addGrid) return;
      addGrid.innerHTML = situationList.map((situation) => {
        const selected = hasSituation(situation.id);
        return `
          <button type="button" class="asb__add-card" data-add-situation="${escapeHTML(situation.id)}" ${selected ? 'disabled' : ''}>
            <strong>${escapeHTML(selected ? `${situation.title} is al toegevoegd` : situation.title)}</strong>
            <span>${escapeHTML(situation.description)}</span>
          </button>
        `;
      }).join('');
      if (addPanel) addPanel.hidden = !state.addPanelOpen;
      if (addToggle) addToggle.setAttribute('aria-expanded', String(state.addPanelOpen));
    };

    const linePriceHtml = (product, quantity, options = {}) => {
      const qty = Number(quantity || 1);
      const price = Number(product?.price_cents || 0);
      const compareAt = Number(product?.compare_at_price_cents || 0);
      const hasCompareAt = compareAt > price;
      const className = options.className || 'asb__line-money';
      const perUnit = options.showUnit && qty > 1 ? `<span>${formatMoney(price)} per stuk</span>` : '';
      return `
        <div class="${escapeHTML(className)}${hasCompareAt ? ' is-sale' : ''}">
          ${hasCompareAt ? `<s>${formatMoney(compareAt * qty)}</s>` : ''}
          <strong>${formatMoney(price * qty)}</strong>
          ${perUnit}
        </div>
      `;
    };

    const selectionLineHtml = (line) => {
      const product = line.product;
      const categoriesCopy = unique(line.categoryTitles).join(', ');
      const variant = product.variant_title ? `<span>${escapeHTML(product.variant_title)}</span>` : '';
      const categoryId = line.categoryIds[0];
      return `
        <article class="asb__selection-line" data-line-category="${escapeHTML(categoryId)}">
          ${imageHtml(product)}
          <div class="asb__selection-copy">
            <a href="${escapeHTML(product.url || '#')}">${escapeHTML(product.display_title || product.title)}</a>
            <span>${escapeHTML(categoriesCopy)}${variant ? ` · ${variant}` : ''}</span>
            <div class="asb__line-qty">
              <button type="button" data-qty-action="decrease" data-category-id="${escapeHTML(categoryId)}" aria-label="${escapeHTML(uiCopy.decrease)}">-</button>
              <span>${line.quantity}</span>
              <button type="button" data-qty-action="increase" data-category-id="${escapeHTML(categoryId)}" aria-label="${escapeHTML(uiCopy.increase)}">+</button>
              <button type="button" data-remove-line="${escapeHTML(categoryId)}">${escapeHTML(uiCopy.remove)}</button>
            </div>
          </div>
          ${linePriceHtml(product, line.quantity)}
        </article>
      `;
    };

    const renderTotals = (counts, totals = discountTotals(counts)) => {
      [summaryTotal, summaryTotalFinal, drawerTotal, drawerTotalFinal].forEach((node) => {
        if (node) node.textContent = formatMoney(counts.subtotalCents);
      });

      [summaryTotalOriginal, drawerTotalOriginal].forEach((node) => {
        if (!node) return;
        node.hidden = true;
        node.textContent = '';
      });

      [summarySavings, drawerSavings].forEach((node) => {
        if (!node) return;
        node.hidden = true;
        node.textContent = '';
      });

      if (mobileTotal) mobileTotal.textContent = formatMoney(counts.subtotalCents);
      totalNodes.forEach((node) => {
        node.textContent = formatMoney(counts.subtotalCents);
      });

      countNodes.forEach((node) => {
        node.textContent = uiCopy.items(counts.productUnits);
      });
      subtotalNodes.forEach((node) => {
        node.textContent = formatMoney(counts.subtotalCents);
      });
      productSubtotalLabels.forEach((node) => {
        node.textContent = discountCopy.productSubtotal;
      });
      discountRows.forEach((node) => {
        node.hidden = true;
      });
      discountValues.forEach((node) => {
        node.textContent = '';
      });
      renderCommerceMeta(counts);
    };

    const renderSelectionLines = (snapshot) => {
      const { needs, counts, lines, totals, discount: info } = snapshot;
      const empty = `<p class="asb__empty-selection">${uiCopy.empty}</p>`;
      const html = lines.length ? lines.map(selectionLineHtml).join('') : empty;
      const visible = state.showAllLines ? lines : lines.slice(0, 4);
      const cappedHtml = lines.length ? visible.map(selectionLineHtml).join('') : empty;
      if (summaryLines) summaryLines.innerHTML = cappedHtml;
      if (summaryMore) {
        summaryMore.hidden = lines.length <= 4;
        summaryMore.textContent = state.showAllLines
          ? 'Toon minder'
          : uiCopy.showAll(lines.length);
      }
      if (drawerLines) drawerLines.innerHTML = html;
      renderTotals(counts, totals);

      const upsell = nextSalesSuggestion(needs, counts, info, lines);
      upsellNodes.forEach((node) => {
        node.textContent = upsell;
        node.hidden = !upsell;
      });

      const barHtml = discountBarHtml(counts);
      discountBars.forEach((node) => {
        node.innerHTML = barHtml;
        node.hidden = !barHtml;
      });
      const bannerHtml = bundleBannerHtml(counts);
      bundleBanners.forEach((node) => {
        node.innerHTML = bannerHtml;
        node.hidden = !bannerHtml;
      });
      renderTierModal(counts, info);

      renderHeroBundle(info);
      tierTriggers.forEach((node) => {
        node.hidden = !info.active;
      });
      const flagCopy = info.percentage ? discountCopy.expected(formatPercent(info.percentage)) : '';
      discountFlags.forEach((node) => {
        node.textContent = flagCopy;
        node.hidden = !flagCopy;
      });
      if (mobileFlag) {
        mobileFlag.textContent = info.percentage ? discountCopy.mobileExpected(formatPercent(info.percentage)) : '';
        mobileFlag.hidden = !info.percentage;
      }
      discountLabels.forEach((node) => {
        node.textContent = discountPolicy.title || 'Staffelvoordeel';
      });

      // Niet twee keer dezelfde drempel: als de aanvulling hem al noemt,
      // valt de losse regel weg.
      const nextLine = nextThresholdLine(info);
      const duplicate = Boolean(upsell && nextLine && upsell.startsWith(nextLine));
      discountNextNodes.forEach((node) => {
        node.textContent = nextLine;
        node.hidden = !nextLine || !counts.productLines || duplicate;
      });

      if (mobileBar) mobileBar.hidden = !state.selectedSituationIds.length || ['situations', 'questions', 'cart'].includes(state.view);
      if (mobileCount) mobileCount.textContent = uiCopy.products(counts.productUnits);
    };

    const renderSummary = (snapshot) => {
      const { counts, signature } = snapshot;
      const selected = activeSituations();
      const title = selected.length === 0
        ? uiCopy.noSituation
        : selected.length === 1 ? selected[0].title : `${selected.length} situaties gecombineerd`;
      const durationCopy = hasOutdoor() ? ` · ${durationOption().title}` : '';

      if (summaryTitle) summaryTitle.textContent = title;
      if (summaryMeta) summaryMeta.textContent = selected.length ? `${peopleLabel()}${durationCopy}` : uiCopy.chooseSituation;
      if (summaryNeeds) summaryNeeds.textContent = String(counts.progressTotal);
      if (summarySelf) summarySelf.textContent = String(counts.selfOpen);
      if (summaryOpen) summaryOpen.textContent = String(counts.open);
      renderSelectionLines(snapshot);

      checkoutLinks.forEach((link) => {
        link.hidden = !(signature && signature === state.lastAddedSignature);
      });
      root.querySelectorAll('[data-cart-add]').forEach((button) => {
        const alreadyAdded = Boolean(signature && signature === state.lastAddedSignature);
        button.disabled = cartBusy || !counts.productLines || alreadyAdded;
        if (cartBusy) {
          button.textContent = 'Toevoegen...';
        } else if (alreadyAdded) {
          button.textContent = uiCopy.selectionAdded;
        } else if (button.dataset.cartLabel) {
          button.textContent = button.dataset.cartLabel;
        } else if (counts.productUnits) {
          button.textContent = uiCopy.addProducts(counts.productUnits);
        } else {
          button.textContent = uiCopy.addSelection;
        }
      });
    };

    /* ------------------------------------------------------------------
       Stap 2: één persoonlijk productadvies. Geen niveaus, geen
       categorieflow: alleen de producten die bij de situatie passen.
       ------------------------------------------------------------------ */

    const MAX_RECO_SINGLE = 7;
    const MAX_RECO_COMBINED = 8;

    const parseCriterion = (raw) => {
      const text = String(raw || '').trim();
      const match = text.match(/^([^:]{2,26}):\s*(.+)$/);
      return match ? { label: match[1].trim(), value: match[2].trim() } : { label: '', value: text };
    };

    const requirementForCategory = (categoryId) => requirementList.find((item) => item.category_id === categoryId) || null;
    const needTitleFor = (categoryId) => requirementForCategory(categoryId)?.title || categories.get(categoryId)?.title || '';
    const themeForCategory = (categoryId) => themes.get(categories.get(categoryId)?.theme_id) || null;
    const situationCopy = () => activeSituations().map((item) => item.title).join(' + ');

    /* Aanbevolen hoofdproducten staan per situatie in de config. Bij meerdere
       situaties tellen producten die in meerdere situaties passen eerst;
       daarna geldt de volgorde uit de config. Dubbele producten en dubbele
       behoeften vallen automatisch samen omdat de categorie de sleutel is. */
    const recommendedCategoryIds = () => {
      const score = new Map();
      const order = [];
      activeSituations().forEach((situation) => {
        (situation.recommended_categories || situation.starter_categories || []).forEach((categoryId, index) => {
          if (!categories.has(categoryId)) return;
          if (!order.includes(categoryId)) order.push(categoryId);
          const current = score.get(categoryId) || { hits: 0, rank: index };
          score.set(categoryId, { hits: current.hits + 1, rank: Math.min(current.rank, index) });
        });
      });
      const cap = state.selectedSituationIds.length > 1 ? MAX_RECO_COMBINED : MAX_RECO_SINGLE;
      return order
        .filter((categoryId) => hasSellableChoice(categoryId))
        .sort((a, b) => {
          const scoreA = score.get(a);
          const scoreB = score.get(b);
          return (scoreB.hits - scoreA.hits) || (scoreA.rank - scoreB.rank) || (order.indexOf(a) - order.indexOf(b));
        })
        .slice(0, cap);
    };

    // Aanbevolen producten die wel geconfigureerd zijn maar nu niet leverbaar.
    const unavailableRecommended = () => {
      const ids = [];
      activeSituations().forEach((situation) => {
        (situation.recommended_categories || []).forEach((categoryId) => {
          if (!categories.has(categoryId) || ids.includes(categoryId)) return;
          if (hasSellableChoice(categoryId)) return;
          if (!choicesForCategory(categoryId).length) return;
          ids.push(categoryId);
        });
      });
      return ids;
    };

    // Nul tot twee bonusproducten: nooit tussen de hoofdadviezen, nooit
    // automatisch toegevoegd en alleen als het product echt bestaat.
    const bonusItems = () => {
      const main = recommendedCategoryIds();
      const picked = [];
      activeSituations().forEach((situation) => {
        (situation.bonus || []).forEach((entry) => {
          const categoryId = entry?.category_id;
          if (!categoryId || main.includes(categoryId)) return;
          if (picked.some((item) => item.categoryId === categoryId)) return;
          if (!hasSellableChoice(categoryId)) return;
          picked.push({ categoryId, label: entry.label || 'Handige extra' });
        });
      });
      return picked.slice(0, 2);
    };

    // Maximaal drie vervangende modellen voor dezelfde behoefte.
    const alternativesFor = (categoryId) => {
      const current = selectedChoiceFor(categoryId);
      return choicesForCategory(categoryId)
        .map((raw) => choiceWithVariant(categoryId, raw))
        .filter((choice) => choice && choice.choice_id !== current?.choice_id)
        .slice(0, 3);
    };

    const featureItemsFor = (categoryId, choice, limit = 5) => {
      const items = [];
      const add = (label, value) => {
        const cleanValue = cleanSpecValue(value);
        const cleanLabel = canonicalSpecLabel(label, cleanValue);
        if (!cleanLabel || !cleanValue) return;
        if (cleanLabel === 'Keuzeprofiel') return;
        if (!isCustomerUsefulSpec(cleanLabel, cleanValue)) return;
        if (isAmbiguousCapacitySpec(cleanLabel, cleanValue, choice)) return;
        items.push({ label: cleanLabel, value: cleanValue });
      };

      Object.entries(choice?.specs || {}).forEach(([label, value]) => add(label, value));
      (Array.isArray(choice?.criteria) ? choice.criteria : []).forEach((raw) => {
        const spec = criterionSpecFor(raw);
        if (spec) add(spec.label, spec.value);
      });

      return consolidateSpecItems(items).slice(0, limit);
    };

    const featureListHtml = (items, className) => {
      if (!items.length) return '';
      return `
        <ul class="${escapeHTML(className)}">
          ${items.map((item) => `
            <li class="${item.label ? 'has-label' : 'is-plain'}">
              ${item.label ? `<strong>${escapeHTML(item.label)}</strong>` : ''}
              <span>${escapeHTML(item.value)}</span>
            </li>
          `).join('')}
        </ul>
      `;
    };

    const cleanSpecValue = (value) => String(value ?? '')
      .replace(/\s+/g, ' ')
      .trim();

    const normalizedSpecValue = (value) => cleanSpecValue(value)
      .toLowerCase()
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const firstSpecNumber = (value) => {
      const match = normalizedSpecValue(value).replace(/\./g, '').replace(/,/g, '.').match(/\d+(?:\.\d+)?/);
      return match ? match[0] : '';
    };

    const hasSpecUnit = (value) => /\b(?:liter|ltr|ml|mah|wh|lumen|lm|cm|mm|kg|gram|g|delig)\b/i.test(cleanSpecValue(value));

    const specValueScore = (value) => {
      const clean = cleanSpecValue(value);
      return clean.length + (hasSpecUnit(clean) ? 80 : 0) + (/[a-zÀ-ÿ]/i.test(clean) ? 40 : 0);
    };

    const sameSpecMeaning = (a, b) => {
      const left = normalizedSpecValue(a);
      const right = normalizedSpecValue(b);
      if (!left || !right) return false;
      if (left === right) return true;
      if (left.includes(right) || right.includes(left)) {
        const shorter = left.length < right.length ? left : right;
        return shorter.length >= 2;
      }
      const leftNumber = firstSpecNumber(left);
      const rightNumber = firstSpecNumber(right);
      return Boolean(leftNumber && leftNumber === rightNumber && (hasSpecUnit(left) || hasSpecUnit(right)));
    };

    const mergeSpecValue = (values, value) => {
      const cleanValue = cleanSpecValue(value);
      if (!cleanValue) return values;
      const next = values.slice();
      const duplicateIndex = next.findIndex((item) => sameSpecMeaning(item, cleanValue));
      if (duplicateIndex >= 0) {
        if (specValueScore(cleanValue) > specValueScore(next[duplicateIndex])) next[duplicateIndex] = cleanValue;
        return next;
      }
      next.push(cleanValue);
      return next;
    };

    const consolidateSpecItems = (items) => {
      const byLabel = new Map();
      items.forEach((item) => {
        const label = cleanSpecValue(item?.label);
        const value = cleanSpecValue(item?.value);
        if (!label || !value) return;
        const current = byLabel.get(label) || [];
        byLabel.set(label, mergeSpecValue(current, value));
      });
      return [...byLabel.entries()].map(([label, values]) => ({
        label,
        value: values.join(' · '),
      }));
    };

    const isCustomerUsefulSpec = (label, value) => {
      const cleanLabel = String(label || '').trim();
      const cleanValue = cleanSpecValue(value);
      if (!cleanLabel || !cleanValue || cleanValue === '—') return false;
      const key = toKebab(cleanLabel);
      if (['keuze', 'badge', 'aanbeveling', 'voorraad', 'stock', 'inventory'].includes(key)) return false;
      if (/^(keuze|slimme add-on|aanbevolen|veel voorraad)$/i.test(cleanValue)) return false;
      if (/^(veel voorraad|ruim op voorraad|op voorraad|voldoende voorraad|beschikbaar|leverbaar)$/i.test(cleanValue)) return false;
      if (/^nog\s+\d+\s+op voorraad$/i.test(cleanValue)) return false;
      return true;
    };

    const isAmbiguousCapacitySpec = (label, value, choice) => {
      if (label !== 'Inhoud/capaciteit') return false;
      const lowerValue = cleanSpecValue(value).toLowerCase();
      const normalizedValue = lowerValue.replace(/\./g, '').replace(/,/g, '.');
      const context = [
        choice?.handle,
        choice?.title,
        choice?.display_title,
        ...(Array.isArray(choice?.criteria) ? choice.criteria : []),
      ].join(' ').toLowerCase();
      const looksLikeBatteryProduct = /\bmah\b|powerbank|accu/.test(context);
      const looksLikeBareLargeCapacity = /^\d{4,}(?:\.\d+)?\s*(?:liter|l)?$/.test(normalizedValue);
      return looksLikeBatteryProduct && looksLikeBareLargeCapacity;
    };

    const canonicalSpecLabel = (label, value = '') => {
      const text = String(label || '').trim();
      const key = toKebab(text);
      const valueText = cleanSpecValue(value);
      if (!key) return '';
      if (['inhoud', 'capaciteit', 'volume'].includes(key)) {
        if (/(?:mah\b|\d\s*wh\b)/i.test(valueText)) return 'Accucapaciteit';
        if (/\b(?:lumen|lm)\b/i.test(valueText)) return 'Lichtsterkte';
        return 'Inhoud/capaciteit';
      }
      if (['battery-capacity', 'batterijcapaciteit', 'accu', 'accucapaciteit'].includes(key)) return 'Accucapaciteit';
      if (['ideal-for', 'gebruik', 'geschikt-voor'].includes(key)) return 'Gebruik';
      if (['persons', 'personen'].includes(key)) return translateExact('Personen');
      if (['dimensions', 'afmetingen', 'maat'].includes(key)) return 'Afmetingen';
      if (['weight', 'gewicht'].includes(key)) return 'Gewicht';
      if (['material', 'materiaal'].includes(key)) return 'Materiaal';
      if (['color', 'kleur'].includes(key)) return 'Kleur';
      if (['water-resistance', 'waterbestendigheid', 'waterdichtheid'].includes(key)) return 'Waterbestendigheid';
      if (['compartments', 'compartimenten', 'vakken'].includes(key)) return 'Compartimenten';
      return text;
    };

    const criterionSpecFor = (raw) => {
      const parsed = parseCriterion(raw);
      const value = cleanSpecValue(parsed.value);
      if (!value) return null;
      if (parsed.label) return { label: canonicalSpecLabel(parsed.label, value), value };

      const lower = value.toLowerCase();
      if (/filtercapaciteit/.test(lower)) return { label: 'Filtercapaciteit', value };
      if (/\b\d+(?:[.,]\d+)?\s*(?:mah)\b/i.test(value)) return { label: 'Accucapaciteit', value };
      if (/\b\d+(?:[.,]\d+)?\s*(?:lumen|lm)\b/i.test(value)) return { label: 'Lichtsterkte', value };
      if (/reservoir/.test(lower)) return { label: 'Reservoir', value };
      if (/\b\d+(?:[.,]\d+)?\s*(?:liter|ltr|ml)\b/i.test(value)) return { label: 'Inhoud/capaciteit', value };
      if (/usb|usb-c|solar|opwind|oplaad|laad|pd\b|fast charge/.test(lower)) return { label: 'Laden/voeding', value };
      if (/lichtstand|standen/.test(lower)) return { label: 'Lichtstanden', value };
      if (/am\/fm|sw\/wx|radio|weerfunctie|weerfunctie/.test(lower)) return { label: 'Ontvangst', value };
      if (/gravity|pomp|systeem/.test(lower)) return { label: 'Systeem', value };
      if (/\b\d+[-\s]?delig\b/i.test(value)) return { label: 'Setinhoud', value };
      if (/afmeting|\b\d+\s*x\s*\d+|\bcm\b|\bmm\b/.test(lower)) return { label: 'Afmetingen', value };
      if (/gewicht|\bkg\b|\bgram\b|\bg\b/.test(lower)) return { label: 'Gewicht', value };
      if (/bpa|rvs|nylon|polyester|materiaal/.test(lower)) return { label: 'Materiaal', value };
      if (/kleur|zwart|groen|oranje|transparant/.test(lower)) return { label: 'Kleur', value };
      if (/waterdicht|waterbestendig|ipx/.test(lower)) return { label: 'Waterbestendigheid', value };
      if (/compartiment|vak/.test(lower)) return { label: 'Compartimenten', value };
      if (/compact|formaat|draagbaar|lichtgewicht|tas|auto|outdoor|thuis/.test(lower)) return { label: 'Praktisch gebruik', value };
      return { label: 'Bijzondere eigenschappen', value };
    };

    const comparisonSpecMapFor = (choice) => {
      const specs = new Map();
      const add = (label, value) => {
        const cleanValue = cleanSpecValue(value);
        const cleanLabel = canonicalSpecLabel(label, cleanValue);
        if (!cleanLabel || !cleanValue || cleanValue === '—') return;
        if (!isCustomerUsefulSpec(cleanLabel, cleanValue)) return;
        if (isAmbiguousCapacitySpec(cleanLabel, cleanValue, choice)) return;
        const values = specs.get(cleanLabel) || [];
        specs.set(cleanLabel, mergeSpecValue(values, cleanValue));
      };
      const addProfile = (value) => {
        const cleanValue = cleanSpecValue(value);
        if (!cleanValue || !isCustomerUsefulSpec('Keuzeprofiel', cleanValue)) return;
        const measurement = criterionSpecFor(cleanValue);
        if (measurement && measurement.label !== 'Bijzondere eigenschappen' && measurement.label !== 'Praktisch gebruik') {
          add(measurement.label, measurement.value);
          return;
        }
        add('Keuzeprofiel', cleanValue);
      };

      Object.entries(choice?.specs || {}).forEach(([label, value]) => add(label, value));
      (Array.isArray(choice?.criteria) ? choice.criteria : []).forEach((raw) => {
        const spec = criterionSpecFor(raw);
        if (spec) add(spec.label, spec.value);
      });
      if (choice?.variant_title) add('Variant', choice.variant_title);
      if (choice?.compare_label || choice?.badge) addProfile(choice.compare_label || choice.badge);

      return specs;
    };

    const comparisonRowsFor = (models) => {
      const specMaps = models.map(comparisonSpecMapFor);
      const preferred = [
        'Keuzeprofiel',
        'Inhoud/capaciteit',
        'Accucapaciteit',
        'Lichtsterkte',
        'Laden/voeding',
        'Lichtstanden',
        'Ontvangst',
        'Reservoir',
        'Filtercapaciteit',
        'Systeem',
        'Afmetingen',
        'Gewicht',
        'Materiaal',
        'Kleur',
        'Waterbestendigheid',
        'Compartimenten',
        'Personen',
        'Gebruik',
        'Variant',
        'Setinhoud',
        'Praktisch gebruik',
        'Bijzondere eigenschappen',
        'Specificatie',
      ];
      const labels = unique(preferred.concat(specMaps.flatMap((specMap) => [...specMap.keys()])));
      const rows = labels.map((label) => {
        const values = specMaps.map((specMap) => (specMap.get(label) || []).join(' · '));
        const filled = values.filter(Boolean);
        return {
          label,
          values,
          filledCount: filled.length,
          hasDifference: unique(filled.map((value) => value.toLowerCase())).length > 1,
        };
      });
      const threshold = Math.min(2, models.length);
      const differentiatingRows = rows.filter((row) => row.filledCount >= threshold && row.hasDifference);
      const comparableRows = rows.filter((row) => row.filledCount >= threshold);
      return (differentiatingRows.length ? differentiatingRows : comparableRows).slice(0, 6);
    };

    const isSelectedCategory = (categoryId) => state.categoryStatus[categoryId] === STATUS.SELECTED && hasSellableChoice(categoryId);

    /* Producttegel in het rustige homepage-gidsritme: foto, badge, productinfo
       en vaste acties zonder de productdata of aanbevelingslogica te wijzigen. */
    const recoCardHtml = (categoryId, options = {}) => {
      const category = categories.get(categoryId);
      const choice = selectedChoiceFor(categoryId);
      if (!category || !choice) return '';
      const theme = themeForCategory(categoryId);
      const selected = isSelectedCategory(categoryId);
      const title = choice.display_title || choice.title;
      const reason = choice.reason || choice.sales_note || category.summary || '';
      const categoryLabel = theme?.short_title || theme?.title || category.title;
      const badge = `<span class="asb__rcard-badge">${escapeHTML(categoryLabel)}</span>`;
      const extra = options.label ? `<span class="asb__rcard-extra">${escapeHTML(options.label)}</span>` : '';
      const alternatives = alternativesFor(categoryId);
      const action = cardActionHtml(categoryId, choice, selected);
      const titleNode = `<button type="button" class="asb__rcard-title asb__rcard-title-button" data-quickview-open="${escapeHTML(categoryId)}">${escapeHTML(title)}</button>`;

      return `
        <article class="asb__rcard${selected ? ' is-selected' : ''}" data-category-id="${escapeHTML(categoryId)}" aria-label="${escapeHTML(`${title}, ${needTitleFor(categoryId)}`)}">
          <div class="asb__rcard-media-wrap">
            ${cardImageHtml(categoryId, choice, title)}
            <span class="asb__rcard-badge-wrap">${badge}${extra}</span>
          </div>
          <div class="asb__rcard-body">
            <p class="asb__rcard-need">${escapeHTML(needTitleFor(categoryId))}</p>
            ${titleNode}
            <p class="asb__rcard-reason">${escapeHTML(reason)}</p>
            ${cardPriceNoteHtml(choice)}
            <div class="asb__rcard-action">${action}</div>
            ${cardFooterHtml(categoryId, title, selected, alternatives.length > 0)}
          </div>
        </article>
      `;
    };

    const renderRecommendations = (counts) => {
      const ids = recommendedCategoryIds();
      const bonus = bonusItems();
      const situation = situationCopy();

      if (recoTitle) recoTitle.textContent = situation ? uiCopy.recommendationsFor(situation) : uiCopy.recommendations;
      if (recoSub) {
        recoSub.textContent = ids.length
          ? uiCopy.recommendationSub(ids.length)
          : uiCopy.chooseFirst;
      }
      if (recoMeta) {
        const facts = [situation || uiCopy.noSituation, peopleLabel()];
        if (hasOutdoor()) facts.push(durationOption().title);
        facts.push(uiCopy.recommendedProducts(ids.length));
        recoMeta.innerHTML = `${facts.map((fact) => `<span>${escapeHTML(fact)}</span>`).join('')}<button type="button" class="asb__reco-edit" data-edit-questions>${escapeHTML(uiCopy.editSituation)}</button>`;
      }

      // Beeld van de eerst gekozen situatie, zoals op de situatiekaart.
      if (recoMediaNode) {
        const withImage = activeSituations().find((item) => item.image);
        recoMediaNode.hidden = !withImage;
        recoMediaNode.innerHTML = withImage
          ? `<img src="${escapeHTML(withImage.image)}" alt="${escapeHTML(withImage.image_alt || withImage.title)}" width="220" height="148" loading="lazy" decoding="async">`
          : '';
      }

      if (recoNotice) {
        const skipped = unavailableRecommended();
        recoNotice.hidden = !skipped.length;
        recoNotice.textContent = skipped.length
          ? uiCopy.unavailable(skipped.length)
          : '';
      }

      if (recoGrid) {
        recoGrid.innerHTML = ids.length
          ? ids.map((categoryId) => recoCardHtml(categoryId)).join('')
          : `<p class="asb__need-empty">${escapeHTML(uiCopy.chooseFirst)}</p>`;
      }

      if (bonusBlock) bonusBlock.hidden = !bonus.length;
      if (bonusGrid) bonusGrid.innerHTML = bonus.map((item) => recoCardHtml(item.categoryId, { label: item.label })).join('');
    };

    // Stap 3 houdt de zelf-regelen-lijst; stap 2 heeft een vast tekstblok.
    const renderSelfBlock = (needs) => {
      if (!selfBlocks.length) return;
      const selfNeeds = needs.filter((need) => need.fulfilment_type === 'self');
      const html = selfNeeds.map((need) => `
        <li>
          <strong>${escapeHTML(need.title)}</strong>
          <span>${escapeHTML(describeNeed(need))}</span>
        </li>
      `).join('');
      selfBlocks.forEach((block) => {
        block.hidden = !selfNeeds.length;
        const list = block.querySelector('[data-self-list]');
        if (list) list.innerHTML = html;
      });
    };

    const renderQuickviewPanel = () => {
      if (!quickviewBody) return;
      const categoryId = state.quickviewPanel;
      if (!categoryId) {
        quickviewBody.innerHTML = '';
        return;
      }

      const category = categories.get(categoryId);
      const choice = selectedChoiceFor(categoryId);
      if (!category || !choice) {
        quickviewBody.innerHTML = `<p class="asb__model-empty">${escapeHTML(uiCopy.unavailableProduct)}</p>`;
        return;
      }

      const theme = themeForCategory(categoryId);
      const title = choice.display_title || choice.title;
      const reason = choice.reason || choice.sales_note || category.summary || '';
      const categoryLabel = theme?.short_title || theme?.title || category.title;
      const features = featureItemsFor(categoryId, choice, 5);
      const variantPicker = variantPickerHtml(categoryId, choice);
      const selected = isSelectedCategory(categoryId);
      const productLink = choice.url && choice.url !== '#'
        ? `<a class="asb__quickview-link" href="${escapeHTML(choice.url)}" target="_blank" rel="noopener noreferrer">${escapeHTML(uiCopy.fullProductPage)} <span aria-hidden="true">→</span></a>`
        : '';
      if (quickviewTitleNode) quickviewTitleNode.textContent = title;

      quickviewBody.innerHTML = `
        <div class="asb__quickview-layout">
          <div class="asb__quickview-media">
            ${productImageHtml(choice, title, '(min-width: 760px) 320px, 88vw')}
          </div>
          <div class="asb__quickview-copy">
            <p class="asb__quickview-kicker">${escapeHTML(categoryLabel)}</p>
            <h3>${escapeHTML(title)}</h3>
            ${reason ? `<p class="asb__quickview-text">${escapeHTML(reason)}</p>` : ''}
            ${priceLineHtml(choice, 'asb__quickview-price')}
            ${features.length ? `<div class="asb__quickview-feature-block"><span>${escapeHTML(uiCopy.keyFeatures)}</span>${featureListHtml(features, 'asb__quickview-features')}</div>` : ''}
            ${variantPicker ? `<div class="asb__quickview-variants"><span>${escapeHTML(uiCopy.variant)}</span>${variantPicker}</div>` : ''}
            <div class="asb__quickview-actions">
              ${selected
                ? `<span class="asb__button asb__button--selected asb__quickview-selected">${checkIconHtml()}<span>${escapeHTML(uiCopy.selected)}</span></span>`
                : `<button type="button" class="asb__button asb__button--primary" data-add-category="${escapeHTML(categoryId)}" ${choice.available && choice.variant_id ? '' : 'disabled'}>${escapeHTML(choice.available && choice.variant_id ? uiCopy.add(formatMoney(choice.price_cents || 0)) : uiCopy.unavailableShort)}</button>`}
              ${productLink}
            </div>
          </div>
        </div>
      `;
    };

    const modelProductColumnHtml = (categoryId, choice, isCurrent) => {
      if (!choice) return '';
      const title = choice.display_title || choice.title;
      return `
        <div class="asb__model-product${isCurrent ? ' is-current' : ''}">
          <div class="asb__model-product-media">
            ${productImageHtml(choice, title, '(min-width: 900px) 170px, 46vw')}
          </div>
          ${isCurrent ? `<span class="asb__model-badge">${escapeHTML(uiCopy.currentChoice)}</span>` : '<span class="asb__model-badge asb__model-badge--empty" aria-hidden="true"></span>'}
          <h3>${escapeHTML(title)}</h3>
          ${priceLineHtml(choice, 'asb__model-price')}
          ${isCurrent
            ? `<span class="asb__model-current-note">${escapeHTML(uiCopy.selectedNow)}</span>`
            : `<button type="button" class="asb__button asb__button--primary" data-choose-choice="${escapeHTML(choice.choice_id)}" data-category-id="${escapeHTML(categoryId)}">${escapeHTML(uiCopy.chooseModel(formatMoney(choice.price_cents || 0)))}</button>`}
        </div>
      `;
    };

    const modelComparisonTableHtml = (categoryId, models, current) => {
      const rows = comparisonRowsFor(models);
      if (!models.length) return `<p class="asb__model-empty">${escapeHTML(uiCopy.noAlternative)}</p>`;
      return `
        <div class="asb__model-table-scroll" tabindex="0" aria-label="${escapeHTML(uiCopy.compareProducts)}">
          <div class="asb__model-table" style="--asb-model-count: ${models.length};">
            <div class="asb__model-product-heads">
              ${models.map((choice) => modelProductColumnHtml(categoryId, choice, choice.choice_id === current?.choice_id)).join('')}
            </div>
            <div class="asb__model-spec-rows">
              ${rows.map((row) => `
                <div class="asb__model-row-label">${escapeHTML(row.label)}</div>
                ${row.values.map((value, index) => `
                  <div class="asb__model-value${value ? '' : ' is-empty'}${row.hasDifference && value && index > 0 ? ' is-different' : ''}">
                    ${value ? escapeHTML(value) : '<span aria-label="Niet opgegeven">—</span>'}
                  </div>
                `).join('')}
              `).join('')}
            </div>
          </div>
        </div>
      `;
    };

    /* "Kies een ander model": ruim vergelijkingsprototype met maximaal drie
       alternatieven voor exact dezelfde behoefte. */
    const renderModelPanel = () => {
      if (!compareBody) return;
      const categoryId = state.comparePanel;
      if (!categoryId) {
        compareBody.innerHTML = '';
        return;
      }
      const current = selectedChoiceFor(categoryId);
      const alternatives = alternativesFor(categoryId);
      const models = [current].concat(alternatives).filter(Boolean).slice(0, 4);
      if (compareTitleNode) compareTitleNode.textContent = uiCopy.compareOther;

      compareBody.innerHTML = `
        <div class="asb__model-intro">
          <p>${escapeHTML(uiCopy.compareIntro)}</p>
          <span>Voor: <b>${escapeHTML(needTitleFor(categoryId) || categories.get(categoryId)?.title || 'dit onderdeel')}</b></span>
        </div>
        ${modelComparisonTableHtml(categoryId, models, current)}
      `;
    };

    /* ------------------------------------------------------------------
       Stap 3: winkelwagen op volle breedte. Hierna volgt de echte
       betaalpagina van Shopify.
       ------------------------------------------------------------------ */

    const cartLineHtml = (line) => {
      const product = line.product;
      const categoryId = line.categoryIds[0];
      const picker = variantPickerHtml(categoryId, product);
      const category = categories.get(categoryId);
      return `
        <article class="asb__cart-line" data-line-category="${escapeHTML(categoryId)}">
          <a class="asb__cart-media" href="${escapeHTML(product.url || '#')}" aria-label="${escapeHTML(product.display_title || product.title)} bekijken">
            ${product.image ? `<img src="${escapeHTML(product.image)}" alt="" loading="lazy">` : '<span>ALFA</span>'}
          </a>
          <div class="asb__cart-info">
            <span class="asb__cart-cat">${escapeHTML(unique(line.categoryTitles).join(', '))}</span>
            <a class="asb__product-title" href="${escapeHTML(product.url || '#')}">${escapeHTML(product.display_title || product.title)}</a>
            ${picker
              ? `<div class="asb__cart-variant"><span>${escapeHTML(uiCopy.variantOrColor)}</span>${picker}</div>`
              : (product.variant_title ? `<span class="asb__cart-variantfixed">${escapeHTML(product.variant_title)}</span>` : '')}
            ${inventoryHtml(product)}
            ${category?.quantity_rule && category.quantity_rule !== 'none' && quantityNote(categoryId)
              ? `<small class="asb__cart-note">${escapeHTML(quantityNote(categoryId))}</small>`
              : ''}
          </div>
          <div class="asb__cart-qty">
            <span class="asb__cart-qtylabel">${escapeHTML(uiCopy.quantity)}</span>
            <div class="asb__qty">
              <button type="button" data-qty-action="decrease" data-category-id="${escapeHTML(categoryId)}" aria-label="${escapeHTML(uiCopy.decrease)}">-</button>
              <span>${line.quantity}</span>
              <button type="button" data-qty-action="increase" data-category-id="${escapeHTML(categoryId)}" aria-label="${escapeHTML(uiCopy.increase)}">+</button>
            </div>
          </div>
          <div class="asb__cart-money">
            ${linePriceHtml(product, line.quantity, { className: 'asb__cart-money-values', showUnit: true })}
            <button type="button" class="asb__text-action" data-remove-line="${escapeHTML(categoryId)}">${escapeHTML(uiCopy.remove)}</button>
          </div>
        </article>
      `;
    };

    const renderCartPage = (snapshot) => {
      if (!cartLinesNode) return;
      const { lines } = snapshot;
      if (cartEmptyNode) cartEmptyNode.hidden = Boolean(lines.length);
      cartLinesNode.hidden = !lines.length;
      cartLinesNode.innerHTML = lines.map(cartLineHtml).join('');
      root.querySelectorAll('[data-checkout-now]').forEach((button) => {
        button.disabled = !lines.length || cartBusy;
        button.textContent = cartBusy ? uiCopy.adding : uiCopy.checkout;
      });
    };

    const renderSeoLinks = () => {
      if (!seoLinksNode) return;
      const links = Array.isArray(config.seo_links) ? config.seo_links : [];
      seoLinksNode.innerHTML = links.map((link) => `<a href="${escapeHTML(link.url)}">${escapeHTML(link.title)}</a>`).join('');
    };

    const render = () => {
      const snapshot = selectionSnapshot();
      const { needs, counts } = snapshot;
      renderSituationCards();
      renderStepBar();
      renderQuestionControls();
      renderAddGrid();
      renderRecommendations(counts);
      renderSelfBlock(needs);
      renderQuickviewPanel();
      renderModelPanel();
      renderCartPage(snapshot);
      renderSummary(snapshot);
      renderSeoLinks();
      if (window.lucide && typeof window.lucide.createIcons === 'function') window.lucide.createIcons();
    };

    const migrateOldState = () => {
      if (isDesignMode()) return null;
      const old = getStoredState(oldStorageKey);
      if (!old) return null;

      const migrated = {
        view: old.view,
        selectedSituationIds: old.selectedSituationIds,
        people: old.people,
        duration: old.duration,
        categoryStatus: {},
      };

      if (old.statuses && typeof old.statuses === 'object') {
        Object.entries(old.statuses).forEach(([needId, oldStatus]) => {
          const requirement = requirements.get(needId);
          const status = OLD_STATUS_MAP[oldStatus];
          if (!requirement || !status) return;
          if (requirement.fulfilment_type === 'self') return;
          if (requirement.category_id && status === STATUS.SELECTED) {
            migrated.categoryStatus[requirement.category_id] = STATUS.SELECTED;
          }
        });
      }
      return migrated;
    };

    const restore = () => {
      const params = new URLSearchParams(window.location.search);
      const deepLink = params.get('situatie') || window.location.hash.replace(/^#situatie-/, '');
      if (deepLink && situations.has(deepLink)) {
        state.selectedSituationIds = [deepLink];
        state.view = 'result';
        return;
      }

      if (isDesignMode()) return;
      const stored = getStoredState(storageKey) || migrateOldState();
      if (!stored) return;

      const ids = validSituationIds(stored.selectedSituationIds);
      if (ids.length) state.selectedSituationIds = ids;
      if (['situations', 'questions', 'result', 'cart'].includes(stored.view)) state.view = stored.view;
      if (['1', '2', '3', '4', '5'].includes(String(stored.people))) state.people = String(stored.people);
      if (durationOptions.some((item) => item.id === stored.duration)) state.duration = stored.duration;
      if (stored.categoryStatus && typeof stored.categoryStatus === 'object') {
        state.categoryStatus = Object.fromEntries(Object.entries(stored.categoryStatus).filter(([id, status]) => categories.has(id) && status === STATUS.SELECTED));
      }
      if (stored.choiceByCategory && typeof stored.choiceByCategory === 'object') {
        state.choiceByCategory = Object.fromEntries(Object.entries(stored.choiceByCategory).filter(([id, choiceId]) => choicesForCategory(id).some((choice) => choice.choice_id === choiceId)));
      }
      if (stored.variantByChoice && typeof stored.variantByChoice === 'object') {
        state.variantByChoice = Object.fromEntries(Object.entries(stored.variantByChoice).filter(([key, variantId]) => {
          const [categoryId, choiceId] = key.split(':');
          const choice = choicesForCategory(categoryId).find((item) => item.choice_id === choiceId);
          return choice && choice.variants.some((variant) => variant.id === Number(variantId) && variant.available);
        }));
      }
      if (stored.qtyByCategory && typeof stored.qtyByCategory === 'object') {
        state.qtyByCategory = Object.fromEntries(Object.entries(stored.qtyByCategory).filter(([id, qty]) => categories.has(id) && Number(qty) > 0));
      }
      if (stored.removedCategories && typeof stored.removedCategories === 'object') {
        state.removedCategories = Object.fromEntries(Object.entries(stored.removedCategories).filter(([id, value]) => categories.has(id) && value === true));
      }
      if (!state.selectedSituationIds.length) state.view = 'situations';
    };

    const focusableElements = (node) => [...node.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((item) => !item.hidden && item.getClientRects().length > 0);

    const openLayer = (layer, panel, trigger) => {
      if (!layer || !panel) return;
      lastFocusedElement = trigger || document.activeElement;
      activeLayer = { layer, panel };
      layer.hidden = false;
      layer.classList.add('is-open');
      root.classList.add('asb--layer-open');
      window.setTimeout(() => {
        const focusable = focusableElements(panel);
        (focusable[0] || panel).focus();
      }, 0);
    };

    const closeLayer = (layer) => {
      const target = layer || activeLayer?.layer;
      if (!target) return;
      if (target === comparePanelNode) state.comparePanel = '';
      if (target === quickviewPanelNode) state.quickviewPanel = '';
      if (target === tierModal && state.tiersOpen) {
        state.tiersOpen = false;
        renderTierModal(countState());
      }
      target.classList.remove('is-open');
      target.hidden = true;
      if (activeLayer?.layer === target) activeLayer = null;
      if (!drawer || drawer.hidden) {
        root.classList.remove('asb--layer-open');
      }
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus({ preventScroll: true });
      }
    };

    const handleAddToCart = async (options = {}) => {
      const openCart = options.openCart !== false;
      const snapshot = selectionSnapshot();
      const { lines, counts, signature } = snapshot;
      const builderDrawerWasOpen = Boolean(drawer && !drawer.hidden);
      if (!lines.length || cartBusy) return false;
      if (signature && signature === state.lastAddedSignature) {
        setCartMessage(`${uiCopy.alreadyAdded} <a href="${escapeHTML(cartUrl)}">${uiCopy.viewCart}</a>`, 'success');
        return true;
      }

      cartBusy = true;
      setCartMessage(uiCopy.addingSelection, '');
      render();

      try {
        const situationTitles = activeSituations().map((item) => item.title).join(', ');
        const bundleKey = makeBundleKey(signature);
        const items = lines.map((line) => ({
          id: Number(line.product.variant_id),
          quantity: Number(line.quantity || 1),
          properties: {
            _Keuzehulp: 'Situatiekeuzehulp',
            _Situaties: situationTitles,
            _Categorie: unique(line.categoryTitles).join(', '),
            _Behoeften: unique(line.needTitles).join(', '),
            ...(line.discountEligible ? {
              _BundleEligible: 'yes',
              _BundleCategory: line.categoryIds[0],
              _BundleKey: bundleKey,
            } : {}),
          },
        }));

        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) throw new Error(`Cart add failed with ${response.status}`);
        await response.json();

        state.lastAddedSignature = signature;
        persist();
        const productCount = lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
        const success = escapeHTML(language === 'nl' && sales.cart_success ? sales.cart_success : uiCopy.added);
        setCartMessage(`${success} <a href="${escapeHTML(cartUrl)}">${uiCopy.viewCart}</a> ${uiCopy.checkoutDirect}`, 'success');
        announce(uiCopy.productsAdded(productCount));
        trackAddToCart(lines, counts, bundleKey);
        document.dispatchEvent(new CustomEvent('alfa:situation-builder:cart-add', { detail: { items, bundleKey } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'alfa_situation_builder' } }));
        updateCartCount();
        if (openCart && !builderDrawerWasOpen) refreshThemeCartDrawer();
        return true;
      } catch (error) {
        const message = escapeHTML(sales.cart_error || 'Toevoegen lukte niet. Probeer opnieuw of open het product.');
        setCartMessage(message, 'error');
        announce(message);
        return false;
      } finally {
        cartBusy = false;
        render();
      }
    };

    root.addEventListener('click', (event) => {
      const closeDrawerTarget = event.target.closest('[data-close-drawer]');
      if (closeDrawerTarget && root.contains(closeDrawerTarget)) {
        closeLayer(drawer);
        return;
      }

      const target = event.target.closest('button');
      if (!target || !root.contains(target) || target.disabled) return;

      if (target.matches('[data-cart-add]')) {
        handleAddToCart({ openCart: true });
        return;
      }

      if (target.matches('[data-open-selection-drawer]')) {
        openLayer(drawer, drawerPanel, target);
        return;
      }

      if (target.matches('[data-situation-card]')) {
        const id = target.dataset.situationId;
        if (!situations.has(id)) return;
        const wasSelected = state.selectedSituationIds.includes(id);
        const first = !state.selectedSituationIds.length;
        state.selectedSituationIds = wasSelected
          ? state.selectedSituationIds.filter((item) => item !== id)
          : state.selectedSituationIds.concat(id);
        state.addPanelOpen = false;
        state.showAllLines = false;
        state.lastAddedSignature = '';
        if (!state.selectedSituationIds.length) {
          state.categoryStatus = {};
          state.choiceByCategory = {};
          state.variantByChoice = {};
          state.qtyByCategory = {};
          state.removedCategories = {};
        }
        announce(wasSelected
          ? `${situations.get(id).title} verwijderd.`
          : uiCopy.situationChosen(situations.get(id).title));
        if (!wasSelected) {
          trackFunnelEvent('situation_selected', {
            situation_id: id,
            situation_title: situations.get(id).title,
            primary: first,
          });
        }
        persist();
        render();
        return;
      }

      if (target.matches('[data-person]')) {
        state.people = target.dataset.person;
        state.lastAddedSignature = '';
        announce(uiCopy.peopleChanged(peopleLabel()));
        persist();
        render();
        return;
      }

      if (target.matches('[data-duration]')) {
        state.duration = target.dataset.duration;
        state.lastAddedSignature = '';
        announce(`Duur aangepast naar ${durationOption().title}.`);
        persist();
        render();
        return;
      }

      if (target.matches('[data-back-to-situations]')) {
        setView('situations', { scroll: true });
        render();
        return;
      }

      if (target.matches('[data-show-result]')) {
        if (!state.selectedSituationIds.length) return;
        setView('result', { scroll: true });
        const resultNeeds = mergedNeeds();
        const resultCounts = countState(resultNeeds);
        trackFunnelEvent('view_result', {
          product_lines: resultCounts.productLines,
          product_units: resultCounts.productUnits,
          subtotal: centsToMajor(resultCounts.subtotalCents),
          open_needs: resultCounts.open,
        });
        announce(uiCopy.readyToReview);
        render();
        return;
      }

      if (target.matches('[data-edit-questions]')) {
        setView('questions', { scroll: true });
        render();
        return;
      }

      if (target.matches('[data-toggle-add-situation]')) {
        state.addPanelOpen = !state.addPanelOpen;
        announce(state.addPanelOpen ? uiCopy.chooseExtra : uiCopy.extraClosed);
        persist();
        render();
        return;
      }

      if (target.matches('[data-add-situation]')) {
        const id = target.dataset.addSituation;
        if (!situations.has(id) || hasSituation(id)) return;
        state.selectedSituationIds.push(id);
        state.addPanelOpen = false;
        state.lastAddedSignature = '';
        announce(`${situations.get(id).title} toegevoegd. Gedeelde behoeften zijn samengevoegd.`);
        trackFunnelEvent('situation_selected', {
          situation_id: id,
          situation_title: situations.get(id).title,
          primary: false,
        });
        persist();
        render();
        return;
      }

      if (target.matches('[data-remove-situation]')) {
        const id = target.dataset.removeSituation;
        state.selectedSituationIds = state.selectedSituationIds.filter((item) => item !== id);
        state.lastAddedSignature = '';
        if (!state.selectedSituationIds.length) {
          state.categoryStatus = {};
          state.choiceByCategory = {};
          state.variantByChoice = {};
          state.qtyByCategory = {};
          setView('situations', { scroll: true, persist: false });
        }
        announce(uiCopy.situationRemoved);
        persist();
        render();
        return;
      }

      if (target.matches('[data-clear-builder]')) {
        state.view = 'situations';
        state.selectedSituationIds = [];
        state.people = '1';
        state.duration = 'dagtocht';
        state.categoryStatus = {};
        state.choiceByCategory = {};
        state.variantByChoice = {};
        state.qtyByCategory = {};
        state.addPanelOpen = false;
        state.removedCategories = {};
        state.showAllLines = false;
        state.lastAddedSignature = '';
        removeStoredState(storageKey);
        setView('situations', { scroll: true, persist: false });
        announce(uiCopy.selectionCleared);
        render();
        return;
      }

      if (target.matches('[data-toggle-all-lines]')) {
        state.showAllLines = !state.showAllLines;
        render();
        return;
      }

      if (target.matches('[data-goto-step]')) {
        const step = target.dataset.gotoStep;
        if (drawer && !drawer.hidden) closeLayer(drawer);
        if (step === 'situation') setView(state.selectedSituationIds.length ? 'questions' : 'situations', { scroll: true });
        if (step === 'equipment' && state.selectedSituationIds.length) setView('result', { scroll: true });
        if (step === 'review' && state.selectedSituationIds.length) setView('cart', { scroll: true });
        render();
        return;
      }

      if (target.matches('[data-checkout-now]')) {
        const snapshot = selectionSnapshot();
        if (!snapshot.lines.length) return;
        const { signature } = snapshot;
        const checkoutWithCurrentCart = () => {
          window.location.assign(`${normalizedRouteRoot}checkout`);
        };
        if (signature && signature === state.lastAddedSignature) {
          checkoutWithCurrentCart();
          return;
        }
        handleAddToCart({ openCart: false }).then((added) => {
          if (added) checkoutWithCurrentCart();
        });
        return;
      }

      if (target.matches('[data-quickview-open]')) {
        const categoryId = target.dataset.quickviewOpen;
        if (!categories.has(categoryId) || !selectedChoiceFor(categoryId)) return;
        state.quickviewPanel = categoryId;
        renderQuickviewPanel();
        openLayer(quickviewPanelNode, quickviewPanelInner, target);
        trackFunnelEvent('quickview_opened', {
          category_id: categoryId,
          category_title: categories.get(categoryId)?.title || categoryId,
        });
        return;
      }

      if (target.matches('[data-close-quickview]')) {
        state.quickviewPanel = '';
        closeLayer(quickviewPanelNode);
        render();
        return;
      }

      if (target.matches('[data-model-open]')) {
        const categoryId = target.dataset.modelOpen;
        state.comparePanel = categoryId;
        renderModelPanel();
        openLayer(comparePanelNode, comparePanelInner, target);
        trackFunnelEvent('compare_opened', {
          category_id: categoryId,
          category_title: categories.get(categoryId)?.title || categoryId,
        });
        return;
      }

      if (target.matches('[data-close-compare]')) {
        state.comparePanel = '';
        closeLayer(comparePanelNode);
        render();
        return;
      }

      if (target.matches('[data-choose-choice]')) {
        const categoryId = target.dataset.categoryId;
        const choiceId = target.dataset.chooseChoice;
        if (!choicesForCategory(categoryId).some((choice) => choice.choice_id === choiceId)) return;
        state.choiceByCategory[categoryId] = choiceId;
        // Stond het oude model in de selectie? Dan neemt het nieuwe model die
        // plek in, met hetzelfde aantal. Stond het er niet in, dan blijft de
        // tegel gewoon op "Toevoegen" staan.
        if (state.comparePanel) {
          state.comparePanel = '';
          closeLayer(comparePanelNode);
        }
        state.lastAddedSignature = '';
        persist();
        const chosen = selectedChoiceFor(categoryId);
        announce(uiCopy.productChosen(chosen?.display_title || chosen?.title || uiCopy.product));
        render();
        return;
      }

      if (target.matches('[data-qty-action]')) {
        const categoryId = target.dataset.categoryId;
        const currentQty = quantityForCategory(categoryId);
        const delta = target.dataset.qtyAction === 'increase' ? 1 : -1;
        setQuantity(categoryId, currentQty + delta);
        return;
      }

      if (target.matches('[data-remove-line]')) {
        setCategorySelected(target.dataset.removeLine, false);
        announce(uiCopy.productRemoved);
        return;
      }

      if (target.matches('[data-add-category]')) {
        const categoryId = target.dataset.addCategory;
        const addFromQuickview = Boolean(target.closest('[data-quickview-panel]'));
        setCategorySelected(categoryId, true);
        const chosen = selectedChoiceFor(categoryId);
        announce(uiCopy.productAdded(chosen?.display_title || chosen?.title || uiCopy.product));
        if (addFromQuickview) {
          closeLayer(quickviewPanelNode);
          window.setTimeout(() => {
            const card = [...root.querySelectorAll('[data-category-id]')]
              .find((item) => item.dataset.categoryId === categoryId);
            card?.querySelector('[data-quickview-open]')?.focus({ preventScroll: true });
          }, 0);
        }
        return;
      }

      if (target.matches('[data-remove-category]')) {
        setCategorySelected(target.dataset.removeCategory, false);
        announce('Uit je pakket verwijderd.');
        return;
      }

      if (target.matches('[data-toggle-tiers]')) {
        state.tiersOpen = !state.tiersOpen;
        render();
        if (!tierModal) return;
        const panel = tierModal.querySelector('.asb__tiers-panel');
        if (state.tiersOpen) openLayer(tierModal, panel, target);
        else closeLayer(tierModal);
        return;
      }

    });

    root.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement) || !target.matches('[data-variant-select]')) return;
      if (!root.contains(target)) return;

      const categoryId = target.dataset.categoryId;
      const choiceId = target.dataset.choiceId;
      const variantId = Number(target.value);
      const choice = choicesForCategory(categoryId).find((item) => item.choice_id === choiceId);
      const variant = choice?.variants?.find((item) => Number(item.id) === variantId && item.available);
      if (!choice || !variant) {
        render();
        return;
      }

      state.variantByChoice[variantStateKey(categoryId, choiceId)] = variantId;
      state.lastAddedSignature = '';
      announce(uiCopy.variantChosen(variant.title || choice.display_title || choice.title));
      persist();
      render();
    });

    document.addEventListener('keydown', (event) => {
      if (!activeLayer) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeLayer(activeLayer.layer);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = focusableElements(activeLayer.panel);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    clearLegacyStoredState();
    restore();
    setView(state.view, { persist: false });
    render();
    updateCartCount();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-situation-builder]').forEach(initBuilder);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
